// Open-Meteo API Service Layer
// Free API — no key required. https://open-meteo.com/

import {
  AerodromeInfo,
  HourlyCondition,
  DailySoaringForecast,
  SoundingLevel,
} from './mockWeatherData';
import { analyzeEmagramHour, dewpointFromTempRh } from './emagramAnalysis';
import { searchLocalAerodromes } from './ourairports';

const BASE_URL = 'https://api.open-meteo.com/v1/forecast';
const DASHBOARD_CACHE_MS = 10 * 60 * 1000;
const WEEK_CACHE_MS = 30 * 60 * 1000;
const STALE_CACHE_MS = 6 * 60 * 60 * 1000;
const RETRY_DELAYS_MS = [30 * 1000, 2 * 60 * 1000, 5 * 60 * 1000];

type OpenMeteoCacheStatus = 'fresh' | 'cached' | 'stale' | 'rate-limited';

interface OpenMeteoCacheMeta {
  status: OpenMeteoCacheStatus;
  fetchedAt: number;
  message?: string;
  retryAfter?: number;
}

interface CachedOpenMeteoResult<T = any> {
  data: T;
  meta: OpenMeteoCacheMeta;
}

interface CachedOpenMeteoParams {
  url: string;
  cacheTtlMs?: number;
  staleTtlMs?: number;
}

interface OpenMeteoCacheEntry {
  data: any;
  fetchedAt: number;
  lastUsedAt: number;
  blockedUntil?: number;
  retryCount: number;
  lastStatus?: number;
}

class OpenMeteoRateLimitError extends Error {
  retryAfter?: number;

  constructor(message: string, retryAfter?: number) {
    super(message);
    this.name = 'OpenMeteoRateLimitError';
    this.retryAfter = retryAfter;
  }
}

const openMeteoCache = new Map<string, OpenMeteoCacheEntry>();
const openMeteoInFlight = new Map<string, Promise<CachedOpenMeteoResult>>();
const OPEN_METEO_STORAGE_PREFIX = 'soarcast:open-meteo:';

function normalizeOpenMeteoCacheKey(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.sort();
    const lat = Number.parseFloat(parsed.searchParams.get('latitude') ?? '');
    const lon = Number.parseFloat(parsed.searchParams.get('longitude') ?? '');
    if (Number.isFinite(lat)) parsed.searchParams.set('latitude', lat.toFixed(4));
    if (Number.isFinite(lon)) parsed.searchParams.set('longitude', lon.toFixed(4));
    parsed.searchParams.sort();
    return parsed.toString();
  } catch {
    return url;
  }
}

function readPersistentOpenMeteoCache(key: string): OpenMeteoCacheEntry | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${OPEN_METEO_STORAGE_PREFIX}${key}`);
    if (!raw) return null;
    const entry = JSON.parse(raw) as OpenMeteoCacheEntry;
    return entry && typeof entry.fetchedAt === 'number' ? entry : null;
  } catch {
    return null;
  }
}

function writePersistentOpenMeteoCache(key: string, entry: OpenMeteoCacheEntry): void {
  if (typeof window === 'undefined' || !entry.data) return;
  try {
    window.localStorage.setItem(`${OPEN_METEO_STORAGE_PREFIX}${key}`, JSON.stringify(entry));
  } catch {
    // Browser storage can be full or disabled; memory cache still protects this session.
  }
}

function retryAfterMs(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (!header) return undefined;
  const seconds = Number.parseInt(header, 10);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const dateMs = Date.parse(header);
  return Number.isFinite(dateMs) ? Math.max(0, dateMs - Date.now()) : undefined;
}

function nextRetryDelay(entry?: OpenMeteoCacheEntry, response?: Response): number {
  const fromHeader = response ? retryAfterMs(response) : undefined;
  if (fromHeader !== undefined) return fromHeader;
  const count = entry?.retryCount ?? 0;
  return RETRY_DELAYS_MS[Math.min(count, RETRY_DELAYS_MS.length - 1)];
}

function cacheMeta(status: OpenMeteoCacheStatus, entry: OpenMeteoCacheEntry, message?: string): OpenMeteoCacheMeta {
  return {
    status,
    fetchedAt: entry.fetchedAt,
    message,
    retryAfter: entry.blockedUntil,
  };
}

export async function getCachedOpenMeteoForecast<T = any>({
  url,
  cacheTtlMs = WEEK_CACHE_MS,
  staleTtlMs = STALE_CACHE_MS,
}: CachedOpenMeteoParams): Promise<CachedOpenMeteoResult<T>> {
  const key = normalizeOpenMeteoCacheKey(url);
  const now = Date.now();
  const cached = openMeteoCache.get(key) ?? readPersistentOpenMeteoCache(key);
  if (cached && !openMeteoCache.has(key)) openMeteoCache.set(key, cached);

  if (cached && now - cached.fetchedAt <= cacheTtlMs) {
    cached.lastUsedAt = now;
    return { data: cached.data as T, meta: cacheMeta('cached', cached, 'Données mises en cache') };
  }

  if (cached?.blockedUntil && now < cached.blockedUntil) {
    if (!cached.data) {
      return {
        data: null as unknown as T,
        meta: {
          status: 'rate-limited',
          fetchedAt: cached.fetchedAt,
          message: 'Open-Meteo limite temporairement les requêtes. Veuillez réessayer dans quelques minutes.',
          retryAfter: cached.blockedUntil,
        },
      };
    }
    cached.lastUsedAt = now;
    return {
      data: cached.data as T,
      meta: cacheMeta(
        'rate-limited',
        cached,
        'Open-Meteo limite temporairement les requêtes. Les dernières données disponibles sont conservées.'
      ),
    };
  }

  const existing = openMeteoInFlight.get(key);
  if (existing) return existing as Promise<CachedOpenMeteoResult<T>>;

  const request = (async (): Promise<CachedOpenMeteoResult<T>> => {
    const res = await fetch(url);
    if (res.status === 429) {
      const retryDelay = nextRetryDelay(cached ?? undefined, res);
      const entry: OpenMeteoCacheEntry = cached ?? {
        data: null,
        fetchedAt: 0,
        lastUsedAt: now,
        retryCount: 0,
      };
      entry.retryCount += 1;
      entry.blockedUntil = Date.now() + retryDelay;
      entry.lastStatus = 429;
      openMeteoCache.set(key, entry);
      writePersistentOpenMeteoCache(key, entry);
      if (cached?.data && now - cached.fetchedAt <= staleTtlMs) {
        return {
          data: cached.data as T,
          meta: cacheMeta(
            'rate-limited',
            entry,
            'Open-Meteo limite temporairement les requêtes. Les dernières données disponibles sont conservées.'
          ),
        };
      }
      return {
        data: null as unknown as T,
        meta: {
          status: 'rate-limited',
          fetchedAt: entry.fetchedAt,
          message: 'Open-Meteo limite temporairement les requêtes. Veuillez réessayer dans quelques minutes.',
          retryAfter: entry.blockedUntil,
        },
      };
    }

    if (!res.ok) {
      if (cached?.data && now - cached.fetchedAt <= staleTtlMs) {
        cached.lastUsedAt = now;
        return {
          data: cached.data as T,
          meta: cacheMeta('stale', cached, 'Données mises en cache utilisées temporairement.'),
        };
      }
      throw new Error(`Open-Meteo API error: ${res.status}`);
    }

      let data = await res.json();
    const entry: OpenMeteoCacheEntry = {
      data,
      fetchedAt: Date.now(),
      lastUsedAt: Date.now(),
      retryCount: 0,
    };
    openMeteoCache.set(key, entry);
    writePersistentOpenMeteoCache(key, entry);
    return { data, meta: cacheMeta('fresh', entry) };
  })().finally(() => {
    openMeteoInFlight.delete(key);
  });

  openMeteoInFlight.set(key, request);
  return request;
}

// ─── In-memory cache ──────────────────────────────────────────────────────────
// Caches raw API responses for 10 minutes to avoid hammering the free tier.
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
interface CacheEntry {
  data: any;
  ts: number;
}
const _apiCache = new Map<string, CacheEntry>();

function getCached(url: string): any | null {
  const entry = _apiCache.get(url);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    _apiCache.delete(url);
    return null;
  }
  return entry.data;
}

function setCache(url: string, data: any): void {
  _apiCache.set(url, { data, ts: Date.now() });
}

// ─── Fetch with retry + back-off ──────────────────────────────────────────────
// Retries up to `maxRetries` times on 429 / 5xx, with exponential back-off.
async function fetchWithRetry(url: string, maxRetries = 3): Promise<Response> {
  const cached = getCached(url);
  if (cached !== null) {
    // Return a synthetic Response wrapping the cached JSON
    return new Response(JSON.stringify(cached), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  let lastError: Error | null = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      // Exponential back-off: 1s, 2s, 4s
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
    }
    try {
      let res = await fetch(url);
      if (res.status === 429 || res.status >= 500) {
        lastError = new Error(`Open-Meteo API error: ${res.status}`);
        if (attempt < maxRetries) continue;
        return res; // let caller handle the non-ok response
      }
      if (res.ok) {
        // Clone to read JSON for cache, then return a fresh Response
        let data = await res.json();
        setCache(url, data);
        return new Response(JSON.stringify(data), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      return res;
    } catch (err) {
      lastError = err as Error;
      if (attempt === maxRetries) throw lastError;
    }
  }
  throw lastError ?? new Error('fetchWithRetry: unknown error');
}

type WeatherSourceId =
  | 'canada-gem' |'france-meteofrance' |'usa-noaa' |'europe-best-match' |'global-best-match' |'global-gfs' |'global-ecmwf' |'global-icon';

interface WeatherSourceConfig {
  id: WeatherSourceId;
  endpoint: string;
  label: string;
  note: string;
  forecastDays: number;
}

const WEATHER_SOURCES: Record<WeatherSourceId, WeatherSourceConfig> = {
  'canada-gem': {
    id: 'canada-gem',
    endpoint: 'https://api.open-meteo.com/v1/gem',
    label: 'Open-Meteo GEM Canada',
    note: 'Source active: GEM Canada/HRDPS quand disponible; donnees publiques Open-Meteo.',
    forecastDays: 7,
  },
  'france-meteofrance': {
    id: 'france-meteofrance',
    endpoint: 'https://api.open-meteo.com/v1/meteofrance',
    label: 'Open-Meteo Meteo-France',
    note: 'Source active: Meteo-France/AROME-ARPEGE quand disponible; jours lointains completes par Open-Meteo global.',
    forecastDays: 4,
  },
  'usa-noaa': {
    id: 'usa-noaa',
    endpoint: 'https://api.open-meteo.com/v1/gfs',
    label: 'Open-Meteo NOAA/GFS',
    note: 'Source active: NOAA/GFS public; pour HRRR/NBM il faudra une couche GRIB serveur plus tard.',
    forecastDays: 7,
  },
  'europe-best-match': {
    id: 'europe-best-match',
    endpoint: BASE_URL,
    label: 'Open-Meteo Europe best-match',
    note: 'Source active: meilleur modele Open-Meteo disponible pour l Europe; donnees indicatives.',
    forecastDays: 7,
  },
  'global-best-match': {
    id: 'global-best-match',
    endpoint: BASE_URL,
    label: 'Open-Meteo monde best-match',
    note: 'Source active: meilleur modele Open-Meteo disponible; resolution variable selon la region.',
    forecastDays: 7,
  },
  'global-gfs': {
    id: 'global-gfs',
    endpoint: 'https://api.open-meteo.com/v1/gfs',
    label: 'Open-Meteo GFS',
    note: 'Modele de controle: GFS global, utile pour verifier la tendance generale.',
    forecastDays: 7,
  },
  'global-ecmwf': {
    id: 'global-ecmwf',
    endpoint: 'https://api.open-meteo.com/v1/ecmwf',
    label: 'Open-Meteo ECMWF',
    note: 'Modele de controle: ECMWF, utile pour comparer plafond, humidite et tendance synoptique.',
    forecastDays: 7,
  },
  'global-icon': {
    id: 'global-icon',
    endpoint: 'https://api.open-meteo.com/v1/dwd-icon',
    label: 'Open-Meteo ICON',
    note: 'Modele de controle: ICON/DWD, utile surtout en Europe quand il est disponible.',
    forecastDays: 7,
  },
};

function isInside(lat: number, lon: number, box: { minLat: number; maxLat: number; minLon: number; maxLon: number }): boolean {
  return lat >= box.minLat && lat <= box.maxLat && lon >= box.minLon && lon <= box.maxLon;
}

function selectWeatherSource(aerodrome: AerodromeInfo): WeatherSourceConfig {
  const country = (aerodrome.country || '').toUpperCase();
  const { lat, lon } = aerodrome;

  const canadaBox = { minLat: 41, maxLat: 84, minLon: -142, maxLon: -52 };
  const franceBox = { minLat: 41, maxLat: 52, minLon: -6, maxLon: 10 };
  const europeBox = { minLat: 34, maxLat: 72, minLon: -25, maxLon: 45 };
  const usaBox = { minLat: 24, maxLat: 50, minLon: -126, maxLon: -66 };

  if (country === 'CA' || isInside(lat, lon, canadaBox)) return WEATHER_SOURCES['canada-gem'];
  if (country === 'FR' || isInside(lat, lon, franceBox)) return WEATHER_SOURCES['france-meteofrance'];
  if (country === 'US' || isInside(lat, lon, usaBox)) return WEATHER_SOURCES['usa-noaa'];
  if (isInside(lat, lon, europeBox)) return WEATHER_SOURCES['europe-best-match'];
  return WEATHER_SOURCES['global-best-match'];
}

function selectControlSources(activeSource: WeatherSourceConfig, aerodrome: AerodromeInfo): WeatherSourceConfig[] {
  const country = (aerodrome.country || '').toUpperCase();
  const { lat, lon } = aerodrome;
  const europeBox = { minLat: 34, maxLat: 72, minLon: -25, maxLon: 45 };
  const canadaBox = { minLat: 41, maxLat: 84, minLon: -142, maxLon: -52 };
  const usaBox = { minLat: 24, maxLat: 50, minLon: -126, maxLon: -66 };

  const sources: WeatherSourceConfig[] = [WEATHER_SOURCES['global-best-match']];

  if (country === 'CA' || isInside(lat, lon, canadaBox)) {
    sources.push(WEATHER_SOURCES['global-gfs'], WEATHER_SOURCES['global-ecmwf']);
  } else if (country === 'US' || isInside(lat, lon, usaBox)) {
    sources.push(WEATHER_SOURCES['global-gfs'], WEATHER_SOURCES['global-ecmwf']);
  } else if (country === 'FR' || isInside(lat, lon, europeBox)) {
    sources.push(WEATHER_SOURCES['global-ecmwf'], WEATHER_SOURCES['global-icon'], WEATHER_SOURCES['global-gfs']);
  } else {
    sources.push(WEATHER_SOURCES['global-ecmwf'], WEATHER_SOURCES['global-gfs']);
  }

  const seen = new Set<WeatherSourceId>([activeSource.id]);
  return sources.filter((source) => {
    if (seen.has(source.id)) return false;
    seen.add(source.id);
    return true;
  });
}

function mergeOpenMeteoByDate(primary: any, fallback: any): any {
  if (!primary?.daily?.time || !fallback?.daily?.time) return primary;

  const existingDates = new Set<string>(primary.daily.time);
  const missingDates = fallback.daily.time.filter((date: string) => !existingDates.has(date));
  if (missingDates.length === 0) return primary;

  const merged = {
    ...primary,
    hourly: { ...primary.hourly },
    daily: { ...primary.daily },
  };

  Object.keys(fallback.daily || {}).forEach((key) => {
    const primaryValues = primary.daily?.[key] || [];
    const fallbackValues = fallback.daily?.[key] || [];
    const additions = fallback.daily.time
      .map((date: string, idx: number) => ({ date, value: fallbackValues[idx] }))
      .filter((item: { date: string }) => missingDates.includes(item.date))
      .map((item: { value: any }) => item.value);
    merged.daily[key] = [...primaryValues, ...additions];
  });

  const missingDateSet = new Set(missingDates);
  Object.keys(fallback.hourly || {}).forEach((key) => {
    const primaryValues = primary.hourly?.[key] || [];
    const fallbackValues = fallback.hourly?.[key] || [];
    const fallbackTimes = fallback.hourly?.time || [];
    const additions = fallbackTimes
      .map((time: string, idx: number) => ({ time, value: fallbackValues[idx] }))
      .filter((item: { time: string }) => missingDateSet.has(item.time.split('T')[0]))
      .map((item: { value: any }) => item.value);
    merged.hourly[key] = [...primaryValues, ...additions];
  });

  return merged;
}

function hasCoherentSoaringSignal(day: DailySoaringForecast): boolean {
  const convectiveHours = day.hourly.filter((h) => h.isConvective && h.vz >= 0.8);
  const dryEnough = (day.precipTotalMm ?? 0) < 2 && (day.precipProbabilityMax ?? 0) < 45 && day.stormRisk < 45;
  return dryEnough && convectiveHours.length >= 3 && day.maxVZ >= 1.2 && (day.dpvKm ?? 0) >= 25;
}

function shouldUseControlModel(primary: DailySoaringForecast, control: DailySoaringForecast): boolean {
  if (!hasCoherentSoaringSignal(control)) return false;

  const primaryConvectiveHours = primary.hourly.filter((h) => h.isConvective && h.vz >= 0.8).length;
  const primaryDpv = primary.dpvKm ?? 0;
  const controlDpv = control.dpvKm ?? 0;

  const primaryMissedDay =
    primary.maxVZ < 1.2 ||
    primaryDpv < 25 ||
    primaryConvectiveHours < 3;

  if (!primaryMissedDay) return false;

  // Avoid switching to a wildly optimistic control model. The control source
  // must be plausible, not just brighter.
  const controlLooksPlausible =
    control.maxVZ <= 3.2 &&
    control.maxCumulusBase <= 3200 &&
    control.windGust <= 55 &&
    controlDpv <= 130;

  return controlLooksPlausible;
}

function spread(values: Array<number | null | undefined>): number {
  const finite = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (finite.length < 2) return 0;
  return Math.max(...finite) - Math.min(...finite);
}

function assessModelAgreement(
  primaryDay: DailySoaringForecast,
  controls: Array<{ source: WeatherSourceConfig; day: DailySoaringForecast }>,
  primarySource: WeatherSourceConfig,
): { confidence: string; note: string; unstable: boolean; label: string } {
  const allRuns = [{ source: primarySource, day: primaryDay }, ...controls];
  const labels = allRuns.map(({ source }) => source.label);

  if (allRuns.length < 2) {
    return {
      confidence: primaryDay.confidence ?? 'Moderee',
      note: primaryDay.modelSourceNote ?? primarySource.note,
      unstable: false,
      label: primarySource.label,
    };
  }

  const ceilingSpread = Math.round(spread(allRuns.map(({ day }) => day.maxCumulusBase)));
  const vzSpread = spread(allRuns.map(({ day }) => day.maxVZ));
  const dpvSpread = Math.round(spread(allRuns.map(({ day }) => day.dpvKm ?? 0)));
  const rainSpread = Math.round(spread(allRuns.map(({ day }) => day.precipProbabilityMax ?? 0)));
  const verdicts = new Set(allRuns.map(({ day }) => day.verdict));

  const confidence =
    ceilingSpread <= 250 && vzSpread <= 0.3 && dpvSpread <= 20 && rainSpread <= 20 && verdicts.size <= 2
      ? 'Elevee'
      : ceilingSpread <= 400 && vzSpread <= 0.5 && dpvSpread <= 35 && rainSpread <= 30
        ? 'Bonne'
        : ceilingSpread <= 650 && vzSpread <= 0.8 && dpvSpread <= 55
          ? 'Moderee' :'Faible';

  const unstable = confidence === 'Faible';
  const note = `Comparaison multi-modele (${labels.join(' + ')}): ecart plafond ${ceilingSpread} m, VZ ${vzSpread.toFixed(1)} m/s, DPV ${dpvSpread} km, pluie ${rainSpread}%. Confiance ${confidence.toLowerCase()}.`;

  return {
    confidence,
    note,
    unstable,
    label: `Multi-modele: ${labels.join(' + ')}`,
  };
}

function reconcileVisibleSoaringSignal(day: DailySoaringForecast): DailySoaringForecast {
  const hourly = day.hourly ?? [];
  const usableCumulusHours = hourly.filter((h) =>
    h.isConvective &&
    h.vz >= 1.2 &&
    h.skyType === 'cumulus' &&
    h.pilotSkySignal !== 'Cumulus plats sous voile' &&
    (h.cloudCoverMid ?? 0) <= 35 &&
    (h.cloudCoverHigh ?? 0) <= 35
  );
  const convectiveHours = hourly.filter((h) => h.isConvective && h.vz >= 0.8);
  const maxHourlyVz = Math.max(day.maxVZ ?? 0, ...hourly.map((h) => h.vz ?? 0));
  const maxHourlyCbh = Math.max(day.maxCumulusBase ?? 0, ...hourly.map((h) => h.cumulusBase ?? 0));
  const maxHourlyWind = Math.max(day.windSurface.speed ?? 0, ...hourly.map((h) => h.windSpeed ?? 0));
  const maxHourlyMidCloud = Math.max(0, ...usableCumulusHours.map((h) => h.cloudCoverMid ?? 0));
  const maxHourlyHighCloud = Math.max(day.highCloudCover ?? 0, ...usableCumulusHours.map((h) => h.cloudCoverHigh ?? 0));
  const maxHourlyPrecipProb = Math.max(day.precipProbabilityMax ?? 0, ...hourly.map((h) => h.precipProbability ?? 0));
  const hasClubSoaringDay =
    usableCumulusHours.length >= 3 &&
    maxHourlyVz >= 1.6 &&
    maxHourlyCbh >= 900 &&
    maxHourlyWind <= 35 &&
    (day.windGust ?? 0) <= 60 &&
    maxHourlyMidCloud <= 45 &&
    maxHourlyHighCloud <= 45 &&
    maxHourlyPrecipProb < 45 &&
    (day.precipTotalMm ?? 0) < 2 &&
    day.stormRisk < 45;

  if (!hasClubSoaringDay) return day;

  const convectiveWindowHours = Math.max(convectiveHours.length, usableCumulusHours.length);
  const repairedDpv = Math.max(
    day.dpvKm ?? 0,
    estimateDPV(
      maxHourlyVz,
      maxHourlyCbh,
      convectiveWindowHours,
      day.windSurface.speed,
      Math.min(55, Math.max(0, ...usableCumulusHours.map((h) => h.cloudCover ?? 0))),
      maxHourlyPrecipProb,
      day.stormRisk
    )
  );
  const repairedScore = Math.max(day.qualityScore, maxHourlyVz >= 1.9 ? 70 : 62);
  const limitingFactors = [...(day.limitingFactors ?? [])];
  if (!limitingFactors.includes('Cumulus exploitables')) {
    limitingFactors.push('Cumulus exploitables');
  }

  return {
    ...day,
    maxVZ: Math.max(day.maxVZ, Math.round(maxHourlyVz * 10) / 10),
    maxCumulusBase: Math.max(day.maxCumulusBase, Math.round(maxHourlyCbh)),
    qualityScore: repairedScore,
    verdict: repairedScore >= 70 ? 'favorable' : day.verdict === 'unflyable' ? 'marginal' : day.verdict,
    dpvKm: repairedDpv,
    flightPotentialLabel: repairedScore >= 70 ? 'Favorable' : 'Marginal à favorable',
    limitingFactors,
  };
}

function mergeForecastRuns(
  primaryRun: { today: DailySoaringForecast; week: DailySoaringForecast[] },
  controlRuns: Array<{ run: { today: DailySoaringForecast; week: DailySoaringForecast[] }; source: WeatherSourceConfig }>,
  primarySource: WeatherSourceConfig,
): { today: DailySoaringForecast; week: DailySoaringForecast[] } {
  const week = primaryRun.week.map((primaryDay) => {
    const comparableControls = controlRuns
      .map(({ run, source }) => ({ source, day: run.week.find((controlDay) => controlDay.date === primaryDay.date) }))
      .filter((item): item is { source: WeatherSourceConfig; day: DailySoaringForecast } => Boolean(item.day));

    const candidates = comparableControls
      .filter(({ day }) => shouldUseControlModel(primaryDay, day))
      .sort((a, b) => {
        const aDpv = a.day.dpvKm ?? 0;
        const bDpv = b.day.dpvKm ?? 0;
        return Math.abs(aDpv - 70) - Math.abs(bDpv - 70);
      });

    const selected = candidates[0];
    const baseDay = selected?.day ?? primaryDay;
    const agreement = assessModelAgreement(primaryDay, comparableControls, primarySource);
    const limitingFactors = [...(baseDay.limitingFactors ?? [])];
    if (agreement.unstable && !limitingFactors.includes('Prevision instable entre modeles')) {
      limitingFactors.push('Prevision instable entre modeles');
    }

    const sourceNote = selected
      ? `${agreement.note} Journee retenue depuis ${selected.source.label} car le modele principal ne detectait pas une convection coherente.`
      : agreement.note;

    return {
      ...baseDay,
      limitingFactors,
      modelSourceLabel: agreement.label,
      modelSourceNote: sourceNote,
      confidence: agreement.confidence,
    };
  }).map(reconcileVisibleSoaringSignal);

  return {
    today: week[0] ?? primaryRun.today,
    week,
  };
}

// v1.0.21 bis: keep only the pressure levels needed for soaring/emagram.
const SOUNDING_PRESSURE_LEVELS = [1000, 950, 925, 900, 850, 700, 600, 500];
const FORECAST_PRESSURE_LEVELS = [1000, 950, 925, 900, 850, 700, 600, 500];

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function estimateHighCloudCover(hourly: any, idx: number): number {
  const total = hourly.cloud_cover?.[idx] ?? 0;
  const low = hourly[`cloud_cover_low`]?.[idx] ?? 0;
  const mid = hourly[`cloud_cover_mid`]?.[idx] ?? 0;
  const high = hourly[`cloud_cover_high`]?.[idx] ?? 0;
  const upperRhValues = [500, 450, 400, 350, 300]
    .map((p) => hourly[`relative_humidity_${p}hPa`]?.[idx])
    .filter((value) => Number.isFinite(value));
  const upperRhMax = upperRhValues.length > 0 ? Math.max(...upperRhValues) : 0;
  const upperRhMean = upperRhValues.length > 0
    ? upperRhValues.reduce((sum, value) => sum + value, 0) / upperRhValues.length
    : 0;

  // Some Open-Meteo models under-report cloud_cover_high even when total cover
  // shows a veil that is not explained by low or mid layers.
  const unexplainedCover = Math.max(0, total - Math.max(low, mid));
  const upperMoistureSignal = upperRhMax >= 55;
  const mostlyUnassignedCloud = total >= 70 && low < 35 && mid < 35;
  const inferredHigh = unexplainedCover >= 25 && high < 25 && (upperMoistureSignal || mostlyUnassignedCloud)
    ? unexplainedCover
    : 0;
  const rhOnlyHigh =
    high < 25 && inferredHigh < 25 && low < 45 && mid < 45
      ? upperRhMax >= 82 || upperRhMean >= 62
        ? 70
        : upperRhMax >= 70 || upperRhMean >= 54
          ? 50
          : upperRhMax >= 58 || upperRhMean >= 46
            ? 32
            : 0
      : 0;

  return Math.round(clamp(Math.max(high, inferredHigh, rhOnlyHigh), 0, 100));
}

function highCloudThermalFactor(highCloudCover: number): number {
  if (highCloudCover >= 80) return 0.62;
  if (highCloudCover >= 60) return 0.72;
  if (highCloudCover >= 40) return 0.84;
  if (highCloudCover >= 25) return 0.92;
  return 1;
}

// Approximate altitude (m) for each pressure level (standard atmosphere)
const PRESSURE_TO_ALT: Record<number, number> = {
  1015: 0, 1000: 110, 985: 240, 970: 370, 950: 500,
  925: 800, 900: 1000, 875: 1200, 850: 1500, 800: 1900, 750: 2480, 700: 3050, 650: 3650,
  600: 4300, 550: 5000, 500: 5740, 450: 6560, 400: 7450,
  350: 8450, 300: 9600, 250: 10400, 225: 11000, 200: 11800,
};

function degToCompass(deg: number): string {
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];
  return dirs[Math.round(deg / 22.5) % 16];
}

function formatHHMM(isoTime: string): string {
  const parts = isoTime.split('T');
  if (parts.length < 2) return '--:--';
  return parts[1].substring(0, 5);
}

function parseHourDecimal(hhmm: string, fallback: number): number {
  const [h, m] = hhmm.split(':').map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(h)) return fallback;
  return h + (Number.isFinite(m) ? m / 60 : 0);
}

function thermalDayPhaseFactor(hour: number, sunriseHour: number, sunsetHour: number): number {
  const daylight = Math.max(6, sunsetHour - sunriseHour);
  const phase = (hour - sunriseHour) / daylight;
  if (phase < 0.12 || phase > 0.97) return 0;
  if (phase <= 0.55) {
    return clamp((phase - 0.12) / 0.43, 0, 1);
  }
  return clamp(1 - Math.pow((phase - 0.55) / 0.42, 1.2), 0, 1);
}

function classifyRainRisk(precipTotalMm: number, precipProbMax: number, wetWindowHours: number): string {
  if (precipTotalMm >= 8 || precipProbMax >= 80) return 'Pluie probable';
  if (wetWindowHours >= 3 || precipTotalMm >= 2 || precipProbMax >= 55) return 'Averses probables';
  if (precipProbMax >= 30) return 'Averses isolees possibles';
  return 'Pas de pluie significative';
}

function describeRainWindow(wetHours: HourlyCondition[], wetConvectiveHours: HourlyCondition[]): string {
  if (wetHours.length === 0) return 'Pas de pluie detectee pendant la fenetre de vol';
  const first = wetHours[0].hour;
  const last = wetHours[wetHours.length - 1].hour;
  if (wetConvectiveHours.length > 0) {
    const convFirst = wetConvectiveHours[0].hour;
    const convLast = wetConvectiveHours[wetConvectiveHours.length - 1].hour;
    return `Averses possibles pendant la fenetre de vol (${convFirst}-${convLast})`;
  }
  return `Precipitations prevues hors fenetre de vol principale (${first}-${last})`;
}

function buildSoundingFromHourly(
  hourly: any,
  idx: number,
  pressureLevels: number[],
  surfaceTemp: number,
  surfaceDewpoint: number
): SoundingLevel[] {
  return pressureLevels
    .map((p, levelIdx) => {
      const temp = hourly[`temperature_${p}hPa`]?.[idx] ?? null;
      const rh = hourly[`relative_humidity_${p}hPa`]?.[idx] ?? null;
      const windSpeed = hourly[`wind_speed_${p}hPa`]?.[idx] ?? null;
      const windDir = hourly[`wind_direction_${p}hPa`]?.[idx] ?? null;
      const geoHeight = hourly[`geopotential_height_${p}hPa`]?.[idx] ?? PRESSURE_TO_ALT[p] ?? 0;
      const levelTemp = temp !== null ? Math.round(temp * 10) / 10 : surfaceTemp;
      const levelDew = rh !== null ? dewpointFromTempRh(levelTemp, rh) : surfaceDewpoint;

      return {
        pressure: p,
        altitude: Math.round(geoHeight),
        temp: levelIdx === 0 ? Math.round(surfaceTemp * 10) / 10 : levelTemp,
        dewpoint: levelIdx === 0 ? Math.round(surfaceDewpoint * 10) / 10 : levelDew,
        windSpeed: windSpeed !== null ? Math.round(windSpeed) : 0,
        windDir: windDir !== null ? Math.round(windDir) % 360 : 0,
      };
    })
    .sort((a, b) => a.altitude - b.altitude);
}

// ─── Aerological calculation helpers ─────────────────────────────────────────

/**
 * Compute thermal gradient between surface (2m) and 850hPa (~1500m).
 * Standard atmosphere: surface to 850hPa ≈ 1500m altitude difference.
 * Gradient (°C/100m) = (T_surface - T_850) / 15
 * Threshold for instability: > 0.6°C/100m (dry adiabatic lapse rate ≈ 1°C/100m)
 */
function computeThermalGradient(tempSurface: number, temp850: number): number {
  const altDiff100m = 15; // 1500m / 100m
  return Math.round(((tempSurface - temp850) / altDiff100m) * 100) / 100;
}

/**
 * Hennig formula: CBH (m) = (T_surface - T_dewpoint_surface) × 125
 * Returns 0 if spread is too small (< 0.5°C).
 */
function computeHennigCBH(tempSurface: number, dewpointSurface: number): number {
  const spread = tempSurface - dewpointSurface;
  if (spread < 0.5) return 0;
  return Math.round(spread * 125);
}

/**
 * Sky type classification based on relative humidity at CBH altitude.
 */
function classifySkyType(rhAtCBH: number): 'cumulus' | 'blue' {
  return rhAtCBH > 40 ? 'cumulus' : 'blue';
}

function diagnoseSkyType(
  emagramCloudRisk: 'blue' | 'cumulus' | 'spreadout',
  isConvective: boolean,
  rhAtCBH: number,
  lclAGL: number,
  thermalTopAGL: number,
  lowCloudPct: number,
  midCloudPct: number
): 'cumulus' | 'blue' | 'stable' {
  if (!isConvective) return 'stable';

  const reachesCondensation = lclAGL > 150 && thermalTopAGL >= lclAGL;
  const cumulusLayerPresent = lowCloudPct >= 12 && lowCloudPct <= 75;
  const humidCloudBase = rhAtCBH >= 48;

  if (emagramCloudRisk === 'cumulus') return 'cumulus';
  if (emagramCloudRisk === 'spreadout') return 'cumulus';
  if (reachesCondensation && (humidCloudBase || cumulusLayerPresent)) return 'cumulus';

  // Mid-level cloud alone does not prove cumulus in the convective layer.
  if (!reachesCondensation && lowCloudPct < 12 && midCloudPct > 45) return 'blue';

  return 'blue';
}

function diagnosePilotSkySignal(
  isConvective: boolean,
  skyType: 'cumulus' | 'blue' | 'stable',
  lowCloudPct: number,
  midCloudPct: number,
  highCloudPct: number,
  windSpeedKmh: number,
  windGustKmh: number,
  rhAtCBH: number,
  cape: number,
  liftedIndex: number,
  phaseFactor: number
): string {
  if (!isConvective) {
    if (midCloudPct > 45 || highCloudPct > 55) return 'Voile limitant';
    return 'Stable';
  }

  const gusty = windSpeedKmh >= 35 || windGustKmh >= 50;
  const strongVerticalGrowth = skyType === 'cumulus' && lowCloudPct >= 45 && rhAtCBH >= 72 && (cape >= 350 || liftedIndex <= -2.5) && phaseFactor < 0.7;
  const veilAboveCu = skyType === 'cumulus' && (midCloudPct >= 45 || highCloudPct >= 55);
  const flatCumulus = skyType === 'cumulus' && lowCloudPct > 0 && lowCloudPct < 35 && (midCloudPct >= 35 || highCloudPct >= 45);

  if (strongVerticalGrowth) return 'Developpement vertical rapide';
  if (veilAboveCu || flatCumulus) return 'Cumulus plats sous voile';
  if (skyType === 'cumulus' && gusty) return 'Cumulus haches par le vent';
  if (skyType === 'cumulus') return 'Cumulus exploitables';
  if (skyType === 'blue' && gusty) return 'Thermiques purs haches';
  if (skyType === 'blue') return 'Thermiques purs';
  return 'Stable';
}

/**
 * Estimate relative humidity at CBH altitude by interpolating between
 * surface (2m) and 850hPa levels.
 */
function estimateRHatCBH(rhSurface: number, rh850: number, cbhMeters: number): number {
  const t = Math.min(1, Math.max(0, cbhMeters / 1500));
  return Math.round(rhSurface + t * (rh850 - rhSurface));
}

/**
 * Estimate unstable layer thickness (m) from surface to the level where
 * the environmental lapse rate drops below dry adiabatic (1°C/100m).
 */
function computeUnstableLayerThickness(
  tempSurface: number,
  temp925: number | null,
  temp850: number | null,
  temp700: number | null
): number {
  let thickness = 0;

  if (temp925 !== null) {
    const grad1 = (tempSurface - temp925) / 8;
    if (grad1 >= 0.6) thickness = 800;
    else return thickness;
  }

  if (temp850 !== null && thickness >= 800) {
    const grad2 = (temp925! - temp850) / 7;
    if (grad2 >= 0.6) thickness = 1500;
    else return thickness;
  }

  if (temp700 !== null && thickness >= 1500) {
    const grad3 = (temp850! - temp700) / 15.5;
    if (grad3 >= 0.6) thickness = 3050;
    else return thickness;
  }

  return thickness;
}

/**
 * Estimate VZ (m/s) based on unstable layer thickness.
 * Safety coefficient of 0.8 applied for pilot-exploitable value.
 * High cloud cover (cirrus) reduces VZ: >30% → ×0.85, >60% → ×0.7
 * NOTE: Relaxed from previous version — cirrus filter is gentler to avoid
 * over-penalizing days with partial high cloud.
 */
function estimateVZ(unstableThickness: number, cape: number, highCloudPct: number = 0): number {
  let baseVZ = 0;
  if (unstableThickness < 500) baseVZ = 0;
  else if (unstableThickness < 800) baseVZ = 0.5 + (unstableThickness - 500) / 600;
  else if (unstableThickness < 1000) baseVZ = 1.0 + (unstableThickness - 800) / 400;
  else if (unstableThickness < 1500) baseVZ = 1.5 + (unstableThickness - 1000) / 1000;
  else baseVZ = 2.0 + Math.min(1.0, (unstableThickness - 1500) / 1500);

  // CAPE bonus: sqrt(2*CAPE) is theoretical max updraft; scale down for soaring
  const capeBonus = cape > 50 ? Math.min(0.8, Math.sqrt(cape) / 80) : 0;
  let rawVZ = Math.min(5.0, baseVZ + capeBonus);

  // High cloud (cirrus) filter — gentler than before
  if (highCloudPct > 70) rawVZ *= 0.7;
  else if (highCloudPct > 40) rawVZ *= 0.85;

  // Apply 0.8 safety coefficient — pilot-exploitable value
  return Math.round(rawVZ * 0.8 * 10) / 10;
}

/**
 * Trigger temperature: minimum surface temperature needed for a parcel to
 * rise at least 500m above ground.
 */
function computeTriggerTemp(dewpointSurface: number): number {
  return Math.round((dewpointSurface + 4.0) * 10) / 10;
}

/**
 * Approximate Lifted Index from temperature at 500hPa and surface parcel.
 */
function computeLiftedIndex(
  tempSurface: number,
  dewpointSurface: number,
  temp500: number | null,
  cape: number,
  modelLiftedIndex: number | null = null
): number {
  if (modelLiftedIndex !== null && Number.isFinite(modelLiftedIndex)) {
    return Math.round(modelLiftedIndex * 10) / 10;
  }
  if (cape > 0) {
    return Math.round((-cape / 100) * 10) / 10;
  }
  if (temp500 !== null) {
    const dalr = 9.8;
    const lcl = computeHennigCBH(tempSurface, dewpointSurface);
    const malr = 6.5;
    const tParcel500 = tempSurface - (dalr * lcl / 1000) - (malr * (5740 - lcl) / 1000);
    return Math.round((temp500 - tParcel500) * 10) / 10;
  }
  return 1.0;
}

// ─── DPV estimation ───────────────────────────────────────────────────────────

/**
 * Estimate Distance Potentielle de Vol (DPV) in km.
 * Combines: VZ max, convective window duration, ceiling AGL, wind, cloud cover, rain risk.
 * Calibrated to match Click'n Glide / TopMeteo outputs for CYRC-type conditions.
 *
 * Reference calibration:
 *   VZ 1.4 m/s, ceiling ~1000m AGL, wind 15 km/h, 4h window → ~45 km
 *   VZ 1.9 m/s, ceiling ~1200m AGL, wind 20 km/h, 5h window → ~72 km
 *   VZ 1.8 m/s, ceiling ~1100m AGL, wind 18 km/h, 5h window → ~74 km
 */
function estimateDPV(
  maxVZ: number,
  ceilingAGL: number,
  convectiveWindowHours: number,
  windSurfaceKmh: number,
  totalCloudPct: number,
  precipProbMax: number,
  stormRisk: number
): number {
  if (maxVZ < 0.5 || ceilingAGL < 300 || convectiveWindowHours < 1) return 0;

  // Base DPV from VZ and ceiling
  // Empirical: DPV ≈ VZ × ceiling_factor × time_factor × wind_factor × cloud_factor
  const ceilingFactor = Math.min(1.5, ceilingAGL / 1000); // 1.0 at 1000m, 1.5 at 1500m+
  const timeFactor = Math.min(1.4, convectiveWindowHours / 4); // 1.0 at 4h, 1.4 at 5.6h+
  const baseDPV = maxVZ * 35 * ceilingFactor * timeFactor; // ~35 km per m/s at reference

  // Wind penalty: strong wind reduces cross-country efficiency
  const windPenalty =
    windSurfaceKmh > 45 ? 0.65 :
    windSurfaceKmh > 35 ? 0.85 :
    windSurfaceKmh > 30 ? 0.95 :
    1.0;

  // Cloud cover penalty: >70% total cloud → less solar energy
  const cloudPenalty = totalCloudPct > 70 ? 0.5 : totalCloudPct > 50 ? 0.75 : 1.0;

  // Rain/storm penalty
  const rainPenalty = precipProbMax > 60 || stormRisk > 50 ? 0.3 : precipProbMax > 30 ? 0.7 : 1.0;

  const dpv = baseDPV * windPenalty * cloudPenalty * rainPenalty;
  return Math.round(Math.max(0, dpv));
}

/**
 * Flight potential label based on DPV and verdict.
 */
function computeFlightPotentialLabel(
  qualityScore: number,
  maxVZ: number,
  ceilingAGL: number,
  dpvKm: number,
  stormRisk: number,
  precipProbMax: number = 0,
  precipTotalMm: number = 0,
  totalCloudPct: number = 0
): string {
  if (qualityScore < 15 || maxVZ < 0.3 || ceilingAGL < 200) return 'Non propice';
  if (stormRisk > 60) return 'Risque orageux';
  if (qualityScore > 75) return 'Journee excellente';
  if (qualityScore >= 50) return 'Bonne journee';
  if (qualityScore >= 15) return 'Journee moyenne';
  if (qualityScore >= 70 && dpvKm >= 70 && maxVZ >= 1.9 && ceilingAGL >= 1200 && stormRisk < 45 && precipProbMax < 45 && precipTotalMm < 2) return 'Favorable';
  if (qualityScore >= 45 && maxVZ >= 1.3 && ceilingAGL >= 700 && precipProbMax < 60) return 'Marginal à favorable';
  if (qualityScore >= 45 && maxVZ >= 1.2 && ceilingAGL >= 400 && precipProbMax < 60 && precipTotalMm < 2) return 'Vol local possible';
  if (maxVZ >= 0.8 && ceilingAGL >= 400) return 'Marginal';
  return 'Faible';
}

/**
 * Compute limiting factors list for display.
 */
function computeLimitingFactors(
  maxVZ: number,
  ceilingAGL: number,
  windSurfaceKmh: number,
  windGustKmh: number,
  totalCloudPct: number,
  tTdSpread: number,
  stormRisk: number,
  wind850Kmh: number,
  precipProbMax: number = 0,
  precipTotalMm: number = 0,
  convectiveInhibition: number | null = null,
  boundaryLayerHeight: number | null = null
): string[] {
  const factors: string[] = [];
  if (maxVZ < 0.8) factors.push(`VZ faible (${maxVZ} m/s)`);
  if (ceilingAGL < 500) factors.push(`Plafond bas (${ceilingAGL} m AGL)`);
  if (windSurfaceKmh > 35) factors.push(`Vent soutenu au sol (${windSurfaceKmh} km/h)`);
  if (windGustKmh > 55) factors.push(`Rafales fortes (${windGustKmh} km/h)`);
  if (totalCloudPct > 70) factors.push(`Couverture nuageuse (${totalCloudPct}%)`);
  if (tTdSpread < 4) factors.push(`Écart T/Td critique (${tTdSpread.toFixed(1)}°C)`);
  if (precipTotalMm >= 2) factors.push(`Pluie prevue (${precipTotalMm.toFixed(1)} mm)`);
  else if (precipProbMax >= 45) factors.push(`Averses possibles (${precipProbMax}%)`);
  if (stormRisk > 40) factors.push(`Risque orageux (${stormRisk}%)`);
  if (wind850Kmh > 55) factors.push(`Vent fort 850hPa (${wind850Kmh} km/h)`);
  if (convectiveInhibition !== null && convectiveInhibition > 80) factors.push(`CIN elevee (${Math.round(convectiveInhibition)} J/kg)`);
  if (boundaryLayerHeight !== null && boundaryLayerHeight > 0 && boundaryLayerHeight < 700 && maxVZ < 1.2) factors.push(`Couche limite basse (${Math.round(boundaryLayerHeight)} m)`);
  return factors;
}

// ─── Verdict and quality score ────────────────────────────────────────────────

function computeSoaringVerdict(
  maxCBH: number,
  maxVZ: number,
  stormRisk: number,
  cape: number,
  li: number,
  maxWind: number,
  qualityScore: number,
  totalCloudPct: number,
  precipProbMax: number,
  precipTotalMm: number,
  cumulusUsableHours: number = 0,
  midCloudPct: number = 0,
  highCloudPct: number = 0,
  convectiveInhibition: number | null = null
): DailySoaringForecast['verdict'] {
  if (stormRisk > 60 || cape > 800 || li < -5) return 'storm-risk';
  if (maxCBH < 200 || maxVZ < 0.3 || maxWind > 55) return 'unflyable';
  if (convectiveInhibition !== null && convectiveInhibition > 180 && maxVZ < 1.2) return 'marginal';
  if (precipTotalMm >= 5 || precipProbMax >= 70) return maxCBH >= 700 && maxVZ >= 1.2 ? 'marginal' : 'unflyable';
  if (
    cumulusUsableHours >= 2 &&
    maxVZ >= 1.6 &&
    maxCBH >= 1000 &&
    midCloudPct <= 25 &&
    highCloudPct <= 25 &&
    maxWind <= 38 &&
    qualityScore >= 60 &&
    stormRisk < 45 &&
    precipProbMax < 45 &&
    precipTotalMm < 2
  ) return 'favorable';
  if (maxCBH >= 1600 && maxVZ >= 2.2 && maxWind <= 38 && stormRisk < 45 && precipProbMax < 45 && precipTotalMm < 2) return 'favorable';
  if (maxCBH >= 1200 && maxVZ >= 1.9 && qualityScore >= 70 && maxWind <= 35 && stormRisk < 45 && precipProbMax < 45 && precipTotalMm < 2) return 'favorable';
  if (qualityScore >= 72 && maxCBH >= 1000 && maxVZ >= 1.7 && stormRisk < 35 && precipProbMax < 35 && precipTotalMm < 1) return 'favorable';
  if (maxCBH >= 600 && maxVZ >= 1.0) return 'marginal';
  return 'marginal';
}

/**
 * Recalibrated quality score (v1.2.0).
 *
 * Key changes vs previous version:
 * 1. T/Td spread < 4°C no longer instantly → 0. It applies a heavy penalty instead.
 *    Only truly catastrophic spread (<2°C) → 0.
 * 2. Total cloud cover >80% no longer instantly → 0. It applies a 70% penalty.
 *    Only >95% (completely overcast) → hard cap at 5.
 * 3. Wind at 5000ft now uses 850hPa (~1500m) as primary source, NOT 600hPa.
 *    The 600hPa level is ~4300m — too high for typical soaring assessment.
 *    Threshold raised to 55 km/h (was 45 km/h) since 850hPa winds are lower.
 * 4. Surface wind >25 km/h: penalty reduced from 60% cut to 40% cut.
 * 5. A day with VZ >= 1.3 m/s and ceiling > 700m AGL must score >= 25 (floor).
 * 6. A day with VZ >= 1.7 m/s and ceiling > 850m AGL must score >= 50 (floor).
 */
function computeQualityScore(
  maxCBH: number,
  maxVZ: number,
  stormRisk: number,
  cape: number,
  li: number,
  rhAtCeiling: number,
  windConvectiveLayer: number,
  windShearKmh: number,
  highCloudPct: number = 0,
  windSurfaceSpeed: number = 0,
  windGust: number = 0,
  tTdSpread: number = 10,
  totalCloudPct: number = 0,
  wind850Kmh: number = 0,  // ← now 850hPa (~1500m), NOT 600hPa
  precipProbMax: number = 0,
  precipTotalMm: number = 0,
  convectiveInhibition: number | null = null,
  boundaryLayerHeight: number | null = null
): number {
  const localSoaringCore =
    maxVZ >= 1.3 &&
    maxCBH >= 600 &&
    tTdSpread >= 3 &&
    windGust <= 55 &&
    stormRisk < 60;
  // ── HARD ZERO: only truly catastrophic conditions ─────────────────────────
  // T/Td spread < 2°C → immediate étalement, no flight possible
  if (tTdSpread < 2) return 0;
  // Gusts > 55 km/h → dangerous for gliders
  // Completely overcast (>95%) → no solar energy at all
  const cloudHardStop =
    totalCloudPct > 95 &&
    precipProbMax >= 45 &&
    precipTotalMm >= 1 &&
    (maxCBH < 700 || maxVZ < 0.7);
  if (cloudHardStop && !localSoaringCore) return 5;

  let score = 0;

  // ── Base CBH score (ceiling) — 35 pts max ─────────────────────────────────
  const cbhScore = Math.min(35, (maxCBH / 2500) * 35);

  // ── Base VZ score — 30 pts max ────────────────────────────────────────────
  // High cloud (cirrus) filter: reduces solar heating → weaker thermals
  // Gentler penalty than before: >70% → ×0.7, >40% → ×0.85
  let vzScore = Math.min(30, (maxVZ / 4) * 30);
  if (totalCloudPct > 70) vzScore *= 0.7;
  else if (totalCloudPct > 40) vzScore *= 0.85;

  score += cbhScore + vzScore;

  // ── Storm risk penalty ────────────────────────────────────────────────────
  score -= stormRisk * 0.25;

  // ── CAPE: moderate instability is good, extreme is bad ───────────────────
  if (cape > 0 && cape <= 400) score += Math.min(5, cape / 80); // bonus for moderate CAPE
  if (cape > 600) score -= Math.min(15, (cape - 600) / 100);

  // ── LI bonus for slight instability ──────────────────────────────────────
  if (li < 0 && li > -3) score += Math.abs(li) * 3;

  if (convectiveInhibition !== null) {
    if (convectiveInhibition > 180) score -= 16;
    else if (convectiveInhibition > 100) score -= 9;
    else if (convectiveInhibition > 50) score -= 4;
    else if (convectiveInhibition < 25 && maxVZ >= 1.2) score += 3;
  }
  if (boundaryLayerHeight !== null && boundaryLayerHeight > 0) {
    if (boundaryLayerHeight >= 1700 && maxVZ >= 1.2) score += 5;
    else if (boundaryLayerHeight >= 1100 && maxVZ >= 1.0) score += 3;
    else if (boundaryLayerHeight < 600 && maxVZ < 1.2) score -= 8;
  }

  // ── Humidity penalty: RH at ceiling > 80% → overdevelopment risk ─────────
  if (rhAtCeiling > 80) score -= 3;

  // ── Wind penalty: wind at convective layer (850hPa) > 35 km/h ────────────
  if (windConvectiveLayer > 35) score -= 3;
  else if (windConvectiveLayer > 25) score -= 1;

  // ── Wind shear penalty ────────────────────────────────────────────────────
  if (windShearKmh > 20) score -= 2;
  else if (windShearKmh > 15) score -= 1;

  // ── High cloud (cirrus) penalty ───────────────────────────────────────────
  if (highCloudPct > 60) score -= 3;
  else if (highCloudPct > 40) score -= 1;

  // ── T/Td spread penalty (< 4°C but > 2°C) ────────────────────────────────
  if (tTdSpread < 4) score *= 0.45; // heavy penalty but not zero
  else if (tTdSpread < 5) score *= 0.75;

  // ── Total cloud cover penalty (> 70% but < 95%) ───────────────────────────
  const weakButUsableCore =
    maxVZ >= 0.8 &&
    maxCBH >= 650 &&
    precipProbMax < 35 &&
    precipTotalMm < 1 &&
    windGust <= 55;
  if (totalCloudPct > 90) score *= localSoaringCore ? 0.85 : weakButUsableCore ? 0.7 : 0.45;
  else if (totalCloudPct > 80) score *= localSoaringCore ? 0.9 : weakButUsableCore ? 0.8 : 0.55;
  else if (totalCloudPct > 70) score *= localSoaringCore ? 0.95 : weakButUsableCore ? 0.85 : 0.65;

  if (precipTotalMm >= 5 || precipProbMax >= 70) score = Math.min(score, 30);
  else if (precipTotalMm >= 2 || precipProbMax >= 55) score *= 0.65;
  else if (precipProbMax >= 35) score *= 0.85;

  // ── Surface wind penalty (v1.0.9 — relaxed) ──────────────────────────────
  // Wind > 25 km/h: cut score by 40% (was 60%)
  if (windSurfaceSpeed > 45) score *= 0.65;
  else if (windSurfaceSpeed > 35) score *= 0.82;
  else if (windSurfaceSpeed > 30) score *= 0.92;

  // ── Gusts penalty (40-55 km/h range) ─────────────────────────────────────
  if (windGust > 65) score *= 0.55;
  else if (windGust > 55) score *= 0.75;
  else if (windGust > 45) score *= 0.9;

  // ── Wind at 850hPa (~1500m) — replaces the old 600hPa check ─────────────
  // 850hPa is ~1500m, relevant for soaring layer. Threshold 55 km/h.
  if (wind850Kmh > 65) score *= 0.45;
  else if (wind850Kmh > 55) score *= 0.7;
  else if (wind850Kmh > 45) score *= 0.88;

  const rawScore = Math.max(0, Math.min(100, Math.round(score)));

  // ── FLOOR RULES: prevent over-penalizing flyable days ────────────────────
  // Club-favorable: a 1200m+ ceiling with around 2 m/s is already a clearly
  // exploitable soaring day when rain/storm/wind are not blocking factors.
  if (maxVZ >= 1.8 && maxCBH >= 1200 && tTdSpread >= 3 && windGust <= 55 && stormRisk < 45 && precipProbMax < 45 && precipTotalMm < 2) {
    return Math.max(rawScore, 60);
  }
  // Strong-local: 1000m ceiling with strong, organized thermals is also a good
  // club day if the hourly profile does not show fast degradation.
  if (maxVZ >= 2.4 && maxCBH >= 1000 && tTdSpread >= 3 && windGust <= 55 && stormRisk < 45 && precipProbMax < 45 && precipTotalMm < 2) {
    return Math.max(rawScore, 62);
  }
  // Rule B must be evaluated before Rule A because strong days also satisfy Rule A.
  if (maxVZ >= 1.7 && maxCBH > 850 && tTdSpread >= 3 && windGust <= 55 && stormRisk < 60) {
    return Math.max(rawScore, 60);
  }
  // Rule A: VZ >= 1.3 m/s AND ceiling > 700m AGL → minimum score 25
  if (maxVZ >= 1.3 && maxCBH > 600 && tTdSpread >= 3 && windGust <= 55) {
    return Math.max(rawScore, 52);
  }
  if (weakButUsableCore) {
    return Math.max(rawScore, 42);
  }

  return rawScore;
}

function detectWaveConditions(windAloft5000: number, windSurface: number): boolean {
  return windAloft5000 > 55 && windAloft5000 / Math.max(windSurface, 1) > 1.8;
}

function detectConvergence(windDirSurface: number, windDirAloft: number): boolean {
  const diff = Math.abs(windDirSurface - windDirAloft);
  return diff > 45 && diff < 315;
}

// ─── Main forecast fetch ──────────────────────────────────────────────────────

function applyCacheMetaToRun(
  run: { today: DailySoaringForecast; week: DailySoaringForecast[] },
  meta: OpenMeteoCacheMeta
): { today: DailySoaringForecast; week: DailySoaringForecast[] } {
  const lastUpdated = meta.fetchedAt > 0 ? new Date(meta.fetchedAt).toISOString() : undefined;
  const retryAfter = meta.retryAfter ? new Date(meta.retryAfter).toISOString() : undefined;
  const week = run.week.map((day) => ({
    ...day,
    cacheStatus: meta.status,
    cacheMessage: meta.message,
    lastUpdated,
    retryAfter,
  }));
  return { today: week[0] ?? run.today, week };
}

async function fetchSoaringForecastFromOpenMeteo(
  aerodrome: AerodromeInfo
): Promise<{ today: DailySoaringForecast; week: DailySoaringForecast[] }> {
  const { lat, lon } = aerodrome;
  const weatherSource = selectWeatherSource(aerodrome);

  const hourlyVarsList = [
    'temperature_2m',
    'relative_humidity_2m',
    'dew_point_2m',
    'cloud_cover',
    'cloud_cover_low',
    'cloud_cover_mid',
    'cloud_cover_high',
    'shortwave_radiation',
    'direct_radiation',
    'diffuse_radiation',
    'sunshine_duration',
    'surface_pressure',
    'pressure_msl',
    'wind_speed_10m',
    'wind_speed_80m',
    'wind_speed_120m',
    'wind_direction_10m',
    'wind_direction_80m',
    'wind_direction_120m',
    'wind_gusts_10m',
    'precipitation_probability',
    'precipitation',
    'rain',
    'showers',
    'snowfall',
    'weather_code',
    'visibility',
    'cape',
    'freezing_level_height',
    'boundary_layer_height',
    'convective_inhibition',
    'lifted_index',
  ];
  const hourlyVars = hourlyVarsList.join(',');

  const dailyVars = [
    'sunrise',
    'sunset',
    'temperature_2m_max',
    'temperature_2m_min',
    'precipitation_sum',
    'precipitation_probability_max',
    'wind_speed_10m_max',
    'wind_gusts_10m_max',
    'wind_direction_10m_dominant',
    'weather_code',
    'shortwave_radiation_sum',
    'sunshine_duration',
  ].join(',');

  const makeUrl = (source: WeatherSourceConfig, days: number, hourly: string = hourlyVars) =>
    `${source.endpoint}?latitude=${lat}&longitude=${lon}&hourly=${hourly}&daily=${dailyVars}&wind_speed_unit=kmh&timezone=auto&forecast_days=${days}`;

  const fetchSourceData = async (source: WeatherSourceConfig, days: number): Promise<CachedOpenMeteoResult> =>
    getCachedOpenMeteoForecast({
      url: makeUrl(source, days),
      cacheTtlMs: WEEK_CACHE_MS,
    });

  let activeSource = weatherSource;
  let primaryMeta: OpenMeteoCacheMeta | null = null;
  let data: any = null;

  try {
    const result = await fetchSourceData(weatherSource, weatherSource.forecastDays);
    data = result.data;
    primaryMeta = result.meta;
  } catch (err) {
    if (weatherSource.id === 'global-best-match') throw err;
    activeSource = WEATHER_SOURCES['global-best-match'];
    const result = await fetchSourceData(activeSource, 7);
    data = result.data;
    primaryMeta = result.meta;
  }

  // If rate-limited with no data, return a minimal rate-limited forecast
  if (!data) {
    const rateMeta = primaryMeta ?? { status: 'rate-limited' as const, fetchedAt: Date.now(), message: 'Open-Meteo limite temporairement les requêtes. Veuillez réessayer dans quelques minutes.' };
    const emptyForecast: DailySoaringForecast = {
      date: new Date().toISOString().slice(0, 10),
      verdict: 'unflyable',
      qualityScore: 0,
      hourly: [],
      cacheStatus: 'rate-limited',
      cacheMessage: rateMeta.message ?? 'Open-Meteo limite temporairement les requêtes.',
      modelSourceLabel: 'Open-Meteo',
    } as unknown as DailySoaringForecast;
    return { today: emptyForecast, week: [emptyForecast] };
  }

  const controlResponses: Array<{ data: any; source: WeatherSourceConfig }> = [];
  const controlSources: WeatherSourceConfig[] = [];

  for (const controlSource of controlSources) {
    try {
      let controlResult: CachedOpenMeteoResult | null = null;
      controlResult = await getCachedOpenMeteoForecast({
        url: makeUrl(controlSource, Math.min(controlSource.forecastDays, 7)),
        cacheTtlMs: WEEK_CACHE_MS,
      });

      const controlData = controlResult.data;
      controlResponses.push({ data: controlData, source: controlSource });
      if ((data.daily?.time?.length || 0) < 7) {
        data = mergeOpenMeteoByDate(data, controlData);
      }
    } catch {
      continue;
    }
  }

  const primaryRun = parseOpenMeteoResponse(data, aerodrome, activeSource);
  const cacheMeta = primaryMeta ?? { status: 'fresh' as const, fetchedAt: Date.now() };
  if (controlResponses.length > 0) {
    const controlRuns = controlResponses.map(({ data: controlData, source }) => ({
      source,
      run: parseOpenMeteoResponse(controlData, aerodrome, source),
    }));
    return applyCacheMetaToRun(mergeForecastRuns(primaryRun, controlRuns, activeSource), cacheMeta);
  }

  return applyCacheMetaToRun(primaryRun, cacheMeta);
}

export async function fetchSoaringForecast(
  aerodrome: AerodromeInfo
): Promise<{ today: DailySoaringForecast; week: DailySoaringForecast[] }> {
  return await fetchSoaringForecastFromOpenMeteo(aerodrome);
}

// ─── Parse API response into app data structures ──────────────────────────────

function parseOpenMeteoResponse(
  data: any,
  aerodrome: AerodromeInfo,
  weatherSource: WeatherSourceConfig = WEATHER_SOURCES['global-best-match']
): { today: DailySoaringForecast; week: DailySoaringForecast[] } {
  const hourly = data.hourly;
  const daily = data.daily;
  const times: string[] = hourly.time;

  const week: DailySoaringForecast[] = [];

  for (let dayIdx = 0; dayIdx < (daily.time?.length || 7); dayIdx++) {
    const dateStr: string = daily.time[dayIdx];
    const sunrise = formatHHMM(daily.sunrise[dayIdx] || '');
    const sunset = formatHHMM(daily.sunset[dayIdx] || '');

    const sunriseHour = parseHourDecimal(sunrise, 6);
    const sunsetHour = parseHourDecimal(sunset, 21);

    // Filter hourly indices for this day, only between sunrise and sunset
    const dayHourlyIndices = times
      .map((t, i) => ({ t, i }))
      .filter(({ t }) => {
        const d = t.split('T')[0];
        const h = parseInt(t.split('T')[1]?.split(':')[0] || '0', 10);
        return d === dateStr && h >= sunriseHour && h <= sunsetHour;
      })
      .map(({ i }) => i);

    if (dayHourlyIndices.length === 0) continue;

    const dayCumulusCandidateHours = dayHourlyIndices.filter((idx) => {
      const low = hourly[`cloud_cover_low`]?.[idx] ?? 0;
      const mid = hourly[`cloud_cover_mid`]?.[idx] ?? 0;
      const high = estimateHighCloudCover(hourly, idx);
      const wind = hourly.wind_speed_10m?.[idx] ?? 10;
      const precipProb = hourly.precipitation_probability?.[idx] ?? 0;
      const snowfall = hourly.snowfall?.[idx] ?? 0;
      const rainAmount = hourly.rain?.[idx] ?? hourly.precipitation?.[idx] ?? 0;
      const precipAmount = Math.max(0, rainAmount + (hourly.showers?.[idx] ?? 0) - snowfall);
      return low >= 20 && low <= 80 && mid < 60 && high < 70 && wind <= 32 && precipProb < 50 && precipAmount < 0.1;
    });
    const daySolarHours = dayHourlyIndices.filter((idx) => (hourly.shortwave_radiation?.[idx] ?? 0) >= 160).length;
    const dayCumulusRegime = dayCumulusCandidateHours.length >= 1 && daySolarHours >= 4;

    // ── Build hourly conditions with real aerological calculations ──────────
    const hourlyConditions: HourlyCondition[] = dayHourlyIndices.map((idx) => {
      const timeStr = times[idx];
      const hour = parseInt(timeStr.split('T')[1]?.split(':')[0] || '0', 10);

      // Surface observations
      const tempSurface = hourly.temperature_2m?.[idx] ?? 15;
      const dewpointSurface = hourly.dew_point_2m?.[idx] ?? 8;
      const rhSurface = hourly.relative_humidity_2m?.[idx] ?? 60;
      const cloudCoverRaw = hourly.cloud_cover?.[idx] ?? 30;
      const cloudCoverLowHour = hourly[`cloud_cover_low`]?.[idx] ?? 0;
      const cloudCoverMidHour = hourly[`cloud_cover_mid`]?.[idx] ?? 0;
      const cloudCoverHighHour = estimateHighCloudCover(hourly, idx);
      const cloudCover = Math.max(cloudCoverRaw, cloudCoverLowHour, cloudCoverMidHour, cloudCoverHighHour);
      const shortwaveRadiation = hourly.shortwave_radiation?.[idx] ?? 0;
      const directRadiation = hourly.direct_radiation?.[idx] ?? 0;
      const diffuseRadiation = hourly.diffuse_radiation?.[idx] ?? 0;
      const sunshineDuration = hourly.sunshine_duration?.[idx] ?? 0;
      const windSpeed = hourly.wind_speed_10m?.[idx] ?? 10;
      const windDir = hourly.wind_direction_10m?.[idx] ?? 270;
      const windSpeed120 = hourly.wind_speed_120m?.[idx] ?? null;
      const windDir120 = hourly.wind_direction_120m?.[idx] ?? null;
      const windGustHour = hourly.wind_gusts_10m?.[idx] ?? windSpeed * 1.4;
      const cape = hourly.cape?.[idx] ?? 0;
      const modelLiftedIndex = hourly.lifted_index?.[idx] ?? null;
      const convectiveInhibition = hourly.convective_inhibition?.[idx] ?? null;
      const boundaryLayerHeight = hourly.boundary_layer_height?.[idx] ?? null;
      const precipProb = hourly.precipitation_probability?.[idx] ?? 0;
      const snowfall = hourly.snowfall?.[idx] ?? 0;
      const rainAmount = hourly.rain?.[idx] ?? hourly.precipitation?.[idx] ?? 0;
      const precipAmount = Math.max(0, rainAmount + (hourly.showers?.[idx] ?? 0) - snowfall);
      const visibility = Math.round((hourly.visibility?.[idx] ?? 20000) / 1000);
      const wmoCode = hourly.weather_code?.[idx] ?? 0;

      // Pressure level temperatures for gradient and LI
      const temp850 = hourly[`temperature_850hPa`]?.[idx] ?? null;
      const temp925 = hourly[`temperature_925hPa`]?.[idx] ?? null;
      const temp700 = hourly[`temperature_700hPa`]?.[idx] ?? null;
      const temp500 = hourly[`temperature_500hPa`]?.[idx] ?? null;
      const rh850 = hourly[`relative_humidity_850hPa`]?.[idx] ?? 50;
      const soundingLevels = buildSoundingFromHourly(hourly, idx, FORECAST_PRESSURE_LEVELS, tempSurface, dewpointSurface);
      const emagram = analyzeEmagramHour(soundingLevels);

      // ── 1. Stability filter: gradient surface → 850hPa ──────────────────
      const gradient = temp850 !== null
        ? computeThermalGradient(tempSurface, temp850)
        : 0;
      // Relaxed threshold: 0.5 instead of 0.6 to avoid over-filtering
      const isThermicallyUnstable = gradient > 0.5;

      // ── 2. Hennig formula for CBH ────────────────────────────────────────
      const cbhRaw = computeHennigCBH(tempSurface, dewpointSurface);

      // ── 3. Sky type classification (RH at CBH altitude) ──────────────────
      const rhAtCBH = estimateRHatCBH(rhSurface, rh850, cbhRaw);
      // ── 4. Unstable layer thickness → VZ estimation ──────────────────────
      const unstableThickness = emagram.usableCeilingAGL;
      const highCloudCoverHour = cloudCoverHighHour;
      const highCloudFactor = highCloudThermalFactor(highCloudCoverHour);
      const cinFactor = convectiveInhibition !== null
        ? convectiveInhibition > 180 ? 0.55 : convectiveInhibition > 100 ? 0.72 : convectiveInhibition > 50 ? 0.86 : 1
        : 1;
      const boundaryLayerCeiling = boundaryLayerHeight !== null && boundaryLayerHeight > 0
        ? Math.round(boundaryLayerHeight)
        : 0;
      const vzRaw = Math.round(emagram.maxVz * highCloudFactor * cinFactor * 10) / 10;

      // ── 5. Convective window: must be thermally unstable, not raining ────
      const isRaining = ((wmoCode >= 51 && wmoCode <= 82) || wmoCode >= 95) || precipAmount >= 0.1 || precipProb >= 55;
      const hourlyLi = computeLiftedIndex(tempSurface, dewpointSurface, temp500, cape, modelLiftedIndex);
      const triggerForHour = computeTriggerTemp(dewpointSurface);
      const weakLocalSignal =
        !isRaining &&
        precipProb < 45 &&
        (convectiveInhibition === null || convectiveInhibition < 140) &&
        tempSurface >= triggerForHour - (dayCumulusRegime ? 2.5 : 0.5) &&
        (cape >= 50 || hourlyLi <= -1 || gradient >= 0.35);
      const solarFactor = clamp(shortwaveRadiation / 520, 0.25, 1.25);
      const sunshineFactor = sunshineDuration > 0 ? clamp(sunshineDuration / 3000, 0.55, 1.1) : 0.85;
      const lowCloudPenalty = cloudCoverLowHour > 85 ? 0.35 : cloudCoverLowHour > 65 ? 0.55 : cloudCoverLowHour > 40 ? 0.78 : 1;
      const midCloudPenalty = cloudCoverMidHour > 80 ? 0.65 : cloudCoverMidHour > 55 ? 0.82 : 1;
      const highCloudPenalty = highCloudFactor;
      const sourceEnergyVzRaw = (
        0.35 +
        cape / 900 +
        Math.max(0, -hourlyLi - 0.5) * 0.12 +
        Math.max(0, gradient - 0.2) * 0.55 +
        Math.max(0, tempSurface - triggerForHour + 1) * 0.08
      ) * solarFactor * sunshineFactor * lowCloudPenalty * midCloudPenalty * highCloudPenalty * cinFactor;
      const phaseFactor = thermalDayPhaseFactor(hour, sunriseHour, sunsetHour);
      const lateThermalFactor = clamp(0.35 + phaseFactor * 0.65, 0, 1);
      // This is a fallback when Open-Meteo's coarse vertical profile is too
      // conservative. Keep it modest: strong VZ should come from the emagram
      // or from an organized cumulus signal, not from this broad energy proxy.
      const sourceEnergyVz = Math.min(2.0, sourceEnergyVzRaw * lateThermalFactor);
      const cumulusHourFactor = phaseFactor;
      const organizedCumulusSignal =
        !isRaining &&
        precipProb < 45 &&
        (convectiveInhibition === null || convectiveInhibition < 120) &&
        tempSurface >= triggerForHour - (dayCumulusRegime ? 2.5 : 0.5) &&
        cloudCoverLowHour >= 30 &&
        cloudCoverLowHour <= 75 &&
        cloudCoverMidHour < 55 &&
        cloudCoverHighHour < 65 &&
        windSpeed <= 30 &&
        cumulusHourFactor > 0 &&
        (gradient >= 0.32 || cape >= 40 || hourlyLi <= -0.5);
      const regimeCumulusSignal =
        !organizedCumulusSignal &&
        dayCumulusRegime &&
        !isRaining &&
        precipProb < 45 &&
        (convectiveInhibition === null || convectiveInhibition < 140) &&
        windSpeed <= 30 &&
        cloudCoverMidHour < 60 &&
        cloudCoverHighHour < 70 &&
        shortwaveRadiation >= 160 &&
        tempSurface >= triggerForHour - 3 &&
        phaseFactor > 0.18 &&
        phaseFactor < 0.95;
      const organizedCumulusVz = organizedCumulusSignal
        ? clamp(
            (
              1.55 +
              Math.min(0.35, Math.max(0, cbhRaw - 650) / 2200) +
              Math.max(0, gradient - 0.35) * 0.55
            ) * cumulusHourFactor * highCloudFactor,
            0,
            2.1
          )
        : regimeCumulusSignal
          ? clamp(
              (
                1.05 +
                0.95 * phaseFactor +
                Math.max(0, gradient - 0.25) * 0.35
              ) * highCloudFactor,
              0.6,
              2.0
            )
        : 0;
      const localFallbackSignal = weakLocalSignal || organizedCumulusSignal || regimeCumulusSignal;
      // Energy-based fallback used when the emagram profile is too conservative.
      // Keep pure-thermal estimates moderate unless the profile itself supports
      // strong VZ; organized cumulus streets get a separate local floor.
      const weakLocalVz = localFallbackSignal
        ? Math.round(
            clamp(Math.max(sourceEnergyVz, organizedCumulusVz), 0, 8.0) * 10
          ) / 10
        : 0;
      const roundedEnergyVz = Math.round(sourceEnergyVz * 10) / 10;
      const roundedCumulusVz = organizedCumulusSignal ? Math.round(organizedCumulusVz * 10) / 10 : 0;
      const weakLocalCeiling = localFallbackSignal
        ? Math.round(clamp(Math.max(cbhRaw, emagram.thermalTopAGL, boundaryLayerCeiling, 650), 650, 2200))
        : 0;
      const effectiveVz = Math.max(vzRaw, weakLocalVz);
      const vzReason = effectiveVz === 0
        ? 'none'
        : vzRaw >= weakLocalVz
          ? 'emagram'
          : roundedCumulusVz >= roundedEnergyVz && roundedCumulusVz > 0
            ? 'cumulus' :'energy';
      const effectiveCeiling = emagram.usableCeilingAGL >= 450
        ? Math.max(emagram.usableCeilingAGL, boundaryLayerCeiling)
        : weakLocalCeiling;
      const isEmagramUsable = ['weak', 'usable-blue', 'usable-cumulus', 'overdeveloping'].includes(emagram.status);
      const isConvective = !isRaining && (isEmagramUsable || localFallbackSignal) && effectiveCeiling >= 450 && effectiveVz >= 0.5;
      const effectiveSkyType = diagnoseSkyType(
        emagram.cloudRisk,
        isConvective,
        rhAtCBH,
        emagram.lclAGL,
        Math.max(emagram.thermalTopAGL, weakLocalCeiling),
        cloudCoverLowHour,
        cloudCoverMidHour
      );
      const pilotSkySignal = diagnosePilotSkySignal(
        isConvective,
        effectiveSkyType,
        cloudCoverLowHour,
        cloudCoverMidHour,
        cloudCoverHighHour,
        windSpeed,
        windGustHour,
        rhAtCBH,
        cape,
        hourlyLi,
        phaseFactor
      );

      // Thermal strength: proxy from VZ
      const thermalStrength = isConvective ? Math.min(6, effectiveVz * 1.2) : 0;

      return {
        hour: `${hour.toString().padStart(2, '0')}:00`,
        time: hour,
        temp: tempSurface,
        dewpoint: dewpointSurface,
        cloudCover,
        cloudCoverLow: Math.round(cloudCoverLowHour),
        cloudCoverHigh: Math.round(cloudCoverHighHour),
        cloudCoverMid: Math.round(cloudCoverMidHour),
        shortwaveRadiation: Math.round(shortwaveRadiation),
        directRadiation: Math.round(directRadiation),
        diffuseRadiation: Math.round(diffuseRadiation),
        sunshineDuration: Math.round(sunshineDuration),
        windSpeed: Math.round(windSpeed),
        windDir: Math.round(windDir),
        windSpeed120: windSpeed120 !== null ? Math.round(windSpeed120) : undefined,
        windDir120: windDir120 !== null ? Math.round(windDir120) : undefined,
        thermalStrength: Math.round(thermalStrength * 10) / 10,
        cumulusBase: isConvective ? effectiveCeiling : 0,
        vz: isConvective ? effectiveVz : 0,
        visibility,
        isConvective,
        skyType: effectiveSkyType,
        pilotSkySignal,
        gradient: Math.round(gradient * 100) / 100,
        precipProbability: precipProb,
        precipAmount: Math.round(precipAmount * 10) / 10,
        snowfall: Math.round(snowfall * 10) / 10,
        weatherCode: wmoCode,
        liftedIndex: hourlyLi,
        convectiveInhibition: convectiveInhibition !== null ? Math.round(convectiveInhibition) : null,
        boundaryLayerHeight: boundaryLayerHeight !== null ? Math.round(boundaryLayerHeight) : null,
        emagramStatus: localFallbackSignal && !isEmagramUsable ? 'weak' : emagram.status,
        emagramSummary: localFallbackSignal && !isEmagramUsable
          ? `Activite locale faible possible malgre un profil Open-Meteo grossier pres du sol.`
          : emagram.summary,
        vzEmagram: vzRaw,
        vzEnergy: localFallbackSignal ? roundedEnergyVz : 0,
        vzCumulus: roundedCumulusVz,
        vzReason,
        thermalTop: emagram.thermalTopAGL,
        lcl: emagram.lclAGL,
        inversionBase: emagram.inversionBaseAGL,
        windLayer: emagram.windLayerKmh,
        windShear: emagram.windShearKmh,
      };
    });

    // ── Aggregate daily stats ────────────────────────────────────────────────
    const convectiveHours = hourlyConditions.filter((h) => h.isConvective);
    const maxCBH = Math.max(0, ...hourlyConditions.map((h) => h.cumulusBase));
    const maxVZ = Math.max(0, ...hourlyConditions.map((h) => h.vz));
    const maxThermal = Math.max(0, ...hourlyConditions.map((h) => h.thermalStrength));
    const maxWind = daily.wind_speed_10m_max?.[dayIdx] ?? 20;
    const dailyCape = Math.max(0, ...dayHourlyIndices.map((idx) => hourly.cape?.[idx] ?? 0));
    const dailyCinValues = dayHourlyIndices
      .map((idx) => hourly.convective_inhibition?.[idx])
      .filter((value) => Number.isFinite(value)) as number[];
    const dailyBoundaryLayerValues = dayHourlyIndices
      .map((idx) => hourly.boundary_layer_height?.[idx])
      .filter((value) => Number.isFinite(value)) as number[];
    const dailyConvectiveInhibition = dailyCinValues.length > 0 ? Math.round(Math.min(...dailyCinValues)) : null;
    const dailyBoundaryLayerHeight = dailyBoundaryLayerValues.length > 0 ? Math.round(Math.max(...dailyBoundaryLayerValues)) : null;
    const dailyPrecipProbMax = daily.precipitation_probability_max?.[dayIdx] ?? 0;
    const dailyPrecipTotalMm = Math.round((daily.precipitation_sum?.[dayIdx] ?? 0) * 10) / 10;
    const flightWindowHours = hourlyConditions.filter((h) => h.time >= 9 && h.time <= 18);
    const flightPrecipProbValues = flightWindowHours
      .map((h) => h.precipProbability)
      .filter((value) => Number.isFinite(value)) as number[];
    const precipProbMax = flightPrecipProbValues.length > 0
      ? Math.max(...flightPrecipProbValues)
      : dailyPrecipProbMax;
    const precipTotalMm = flightWindowHours.length > 0
      ? Math.round(flightWindowHours.reduce((sum, h) => sum + (h.precipAmount ?? 0), 0) * 10) / 10
      : dailyPrecipTotalMm;
    const wetHours = hourlyConditions.filter((h) => (h.precipAmount ?? 0) >= 0.1 || (h.precipProbability ?? 0) >= 45);
    const convectiveStartHour = convectiveHours[0]?.time ?? 10;
    const convectiveEndHour = convectiveHours[convectiveHours.length - 1]?.time ?? 20;
    const wetConvectiveHours = wetHours.filter((h) => h.time >= convectiveStartHour && h.time <= convectiveEndHour);
    const rainRiskLabel = classifyRainRisk(precipTotalMm, precipProbMax, wetConvectiveHours.length);
    const rainWindowLabel = describeRainWindow(wetHours, wetConvectiveHours);

    // ── Daily temperature min/max at 2m ─────────────────────────────────────
    const tempMin: number = Math.round((daily.temperature_2m_min?.[dayIdx] ?? 10) * 10) / 10;
    const tempMax: number = Math.round((daily.temperature_2m_max?.[dayIdx] ?? 22) * 10) / 10;

    // ── Lifted Index ─────────────────────────────────────────────────────────
    const middayIdx = dayHourlyIndices[Math.floor(dayHourlyIndices.length / 2)];
    const middayTemp = hourly.temperature_2m?.[middayIdx] ?? 15;
    const middayDew = hourly.dew_point_2m?.[middayIdx] ?? 8;
    const middayTemp500 = hourly[`temperature_500hPa`]?.[middayIdx] ?? null;
    const middayModelLi = hourly.lifted_index?.[middayIdx] ?? null;
    const li = computeLiftedIndex(middayTemp, middayDew, middayTemp500, dailyCape, middayModelLi);

    // ── Wind profile ─────────────────────────────────────────────────────────
    const windSurfaceSpeed = Math.round(hourly.wind_speed_10m?.[middayIdx] ?? 15);
    const windSurfaceDir = degToCompass(hourly.wind_direction_10m?.[middayIdx] ?? 270);
    const windAloft80Speed = Math.round(hourly.wind_speed_80m?.[middayIdx] ?? 30);
    const windAloft80Dir = degToCompass(hourly.wind_direction_80m?.[middayIdx] ?? 260);
    const windAloft120Speed = hourly.wind_speed_120m?.[middayIdx] ?? null;
    const windAloft120Dir = hourly.wind_direction_120m?.[middayIdx] ?? null;

    // ── High cloud cover (cirrus) — use midday value ─────────────────────────
    const highCloudCover: number = estimateHighCloudCover(hourly, middayIdx);
    const highCloudFilter: boolean = highCloudCover > 30;

    // ── Wind gust at surface ─────────────────────────────────────────────────
    const windGustDaily = daily.wind_gusts_10m_max?.[dayIdx] ?? null;
    const windGustHourly = hourly.wind_gusts_10m?.[middayIdx] ?? null;
    const daytimeGustValues = dayHourlyIndices
      .filter((idx) => {
        const hour = Number.parseInt((times[idx].split('T')[1] ?? '00:00').slice(0, 2), 10);
        return hour >= 9 && hour <= 18;
      })
      .map((idx) => hourly.wind_gusts_10m?.[idx])
      .filter((value) => Number.isFinite(value)) as number[];
    const windGust: number = Math.round(
      daytimeGustValues.length > 0
        ? Math.max(...daytimeGustValues)
        : (windGustHourly ?? windGustDaily ?? windSurfaceSpeed * 1.4)
    );
    const lowLevelShear = windAloft120Speed !== null
      ? Math.abs(Math.round(windAloft120Speed - windSurfaceSpeed))
      : 0;

    // ── Storm risk ───────────────────────────────────────────────────────────
    // Storm risk: keep rain/showers separate from true convective signals.
    const instabilityScore = (dailyCape / 1000) * 45 + (li < -2 ? Math.min(25, Math.abs(li + 2) * 7) : 0);
    const convectiveRainBoost = wetConvectiveHours.length > 0 && dailyCape > 150
      ? Math.min(20, precipProbMax * 0.2)
      : 0;
    const cinStormCap = dailyConvectiveInhibition !== null && dailyConvectiveInhibition > 120 ? 55 : 100;
    const stormRisk = Math.min(cinStormCap, Math.round(instabilityScore + convectiveRainBoost));

    // ── Wave and convergence detection ───────────────────────────────────────
    const waveConditions = detectWaveConditions(windAloft80Speed * 1.5, windSurfaceSpeed);
    const convergence = detectConvergence(
      hourly.wind_direction_10m?.[middayIdx] ?? 270,
      hourly.wind_direction_80m?.[middayIdx] ?? 250
    );

    // ── Humidity at ceiling (RH at 850 hPa as proxy for ceiling level) ──────
    const rhAtCeiling: number = hourly[`relative_humidity_850hPa`]?.[middayIdx] ?? 50;

    // ── Wind at convective layer (850 hPa ≈ 1500 m) ──────────────────────────
    const wind850Raw = hourly[`wind_speed_850hPa`]?.[middayIdx] ?? null;
    const windConvectiveKmh: number = wind850Raw !== null
      ? Math.round(wind850Raw)
      : Math.round(hourly.wind_speed_80m?.[middayIdx] ?? 20);

    // ── Wind at 5000ft: use 850hPa (~1500m) as primary, NOT 600hPa ──────────
    // 600hPa ≈ 4300m — too high for typical soaring layer assessment.
    // 850hPa ≈ 1500m — the relevant soaring layer for most glider operations.
    // For display purposes (windAloft5000 label), we use 700hPa (~3050m) as
    // a compromise between "5000ft" and "soaring layer".
    const wind700Raw = hourly[`wind_speed_700hPa`]?.[middayIdx] ?? null;
    const wind700Dir = hourly[`wind_direction_700hPa`]?.[middayIdx] ?? null;
    const wind5000ftSpeed: number = wind700Raw !== null
      ? Math.round(wind700Raw)
      : Math.round(windAloft80Speed * 1.3);
    const wind5000ftDir: string = wind700Dir !== null
      ? degToCompass(wind700Dir)
      : windAloft80Dir;

    // ── Wind shear between 850 hPa and 700 hPa ───────────────────────────────
    const windShearKmh: number = wind700Raw !== null && wind850Raw !== null
      ? Math.abs(Math.round(wind700Raw - wind850Raw))
      : lowLevelShear;

    // ── Overdevelopment risk flag (RH at ceiling > 80%) ──────────────────────
    const emagramOverdevelopment = hourlyConditions.some((h) => h.emagramStatus === 'overdeveloping');
    const overdevelopmentRisk: boolean = rhAtCeiling > 80 || emagramOverdevelopment;

    // ── Wind shear fragmented flag (shear > 15 km/h) ─────────────────────────
    const maxEmagramShear = Math.max(0, ...hourlyConditions.map((h) => h.windShear ?? 0));
    const windShearFragmented: boolean = windShearKmh > 15 || maxEmagramShear > 18;

    // ── T/Td spread at midday ─────────────────────────────────────────────────
    const middayTTdSpread = (hourly.temperature_2m?.[middayIdx] ?? 15) - (hourly.dew_point_2m?.[middayIdx] ?? 8);

    // ── Total cloud cover at midday ───────────────────────────────────────────
    const totalCloudCover: number = Math.round(hourly[`cloud_cover`]?.[middayIdx] ?? 0);
    const midCloudCover: number = Math.round(hourly[`cloud_cover_mid`]?.[middayIdx] ?? 0);

    // ── Quality score — uses 850hPa wind, NOT 600hPa ─────────────────────────
    let qualityScore = computeQualityScore(
      maxCBH, maxVZ, stormRisk, dailyCape, li,
      rhAtCeiling, windConvectiveKmh, windShearKmh,
      highCloudCover, windSurfaceSpeed, windGust,
      middayTTdSpread, totalCloudCover,
      windConvectiveKmh,  // ← 850hPa wind used for altitude wind check
      precipProbMax, precipTotalMm,
      dailyConvectiveInhibition, dailyBoundaryLayerHeight
    );
    const cumulusUsableHours = hourlyConditions.filter((h) => h.pilotSkySignal === 'Cumulus exploitables').length;
    const gustyCumulusHours = hourlyConditions.filter((h) => h.pilotSkySignal === 'Cumulus haches par le vent').length;
    const veiledCumulusHours = hourlyConditions.filter((h) => h.pilotSkySignal === 'Cumulus plats sous voile').length;
    const fastGrowthMainHours = hourlyConditions.filter((h) =>
      h.pilotSkySignal === 'Developpement vertical rapide' &&
      h.time >= 10 &&
      h.time <= 16
    ).length;
    const fastGrowthLateHours = hourlyConditions.filter((h) =>
      h.pilotSkySignal === 'Developpement vertical rapide' &&
      h.time > 16
    ).length;
    const visibleSoaringHours = hourlyConditions.filter((h) =>
      (h.vz ?? 0) >= 1.2 &&
      (h.cumulusBase ?? 0) >= 600 &&
      (h.precipAmount ?? 0) < 0.1 &&
      (h.precipProbability ?? 0) < 45 &&
      (
        h.pilotSkySignal === 'Cumulus exploitables' ||
        h.pilotSkySignal === 'Cumulus haches par le vent' ||
        h.skyType === 'cumulus'
      )
    ).length;
    const localLowCeilingHours = hourlyConditions.filter((h) =>
      (h.vz ?? 0) >= 1.1 &&
      (h.cumulusBase ?? 0) >= 400 &&
      (h.precipAmount ?? 0) < 0.1 &&
      (h.precipProbability ?? 0) < 45 &&
      (h.cloudCoverHigh ?? 0) <= 25 &&
      (
        h.pilotSkySignal === 'Cumulus exploitables' ||
        h.pilotSkySignal === 'Cumulus haches par le vent' ||
        h.skyType === 'cumulus'
      )
    ).length;
    const pilotUsableLocalHours = hourlyConditions.filter((h) =>
      h.time >= 10 &&
      h.time <= 17 &&
      (h.vz ?? 0) >= 1.2 &&
      (h.precipAmount ?? 0) < 0.2 &&
      (h.precipProbability ?? 0) < 50 &&
      (h.cloudCoverHigh ?? 0) <= 45 &&
      (h.cloudCoverMid ?? 0) <= 55 &&
      (h.windSpeed ?? 0) <= 32 &&
      (h.windLayer ?? h.windSpeed ?? 0) <= 48 &&
      (
        h.pilotSkySignal === 'Cumulus exploitables' ||
        h.pilotSkySignal === 'Cumulus haches par le vent' ||
        h.skyType === 'cumulus' ||
        ((h.cloudCoverLow ?? 0) >= 18 && (h.cloudCoverLow ?? 0) <= 70)
      )
    ).length;
    const pilotGoodLocalHours = hourlyConditions.filter((h) =>
      h.time >= 11 &&
      h.time <= 16 &&
      (h.vz ?? 0) >= 1.45 &&
      (h.precipAmount ?? 0) < 0.2 &&
      (h.precipProbability ?? 0) < 45 &&
      (h.cloudCoverHigh ?? 0) <= 35 &&
      (h.cloudCoverMid ?? 0) <= 45 &&
      (h.windSpeed ?? 0) <= 30 &&
      (h.windLayer ?? h.windSpeed ?? 0) <= 45 &&
      (
        h.pilotSkySignal === 'Cumulus exploitables' ||
        h.pilotSkySignal === 'Cumulus haches par le vent' ||
        h.skyType === 'cumulus' ||
        ((h.cloudCoverLow ?? 0) >= 18 && (h.cloudCoverLow ?? 0) <= 65)
      )
    ).length;
    const realLocalSoaringWindow =
      visibleSoaringHours >= 3 &&
      maxVZ >= 1.4 &&
      maxCBH >= 650 &&
      precipProbMax < 45 &&
      precipTotalMm < 2 &&
      stormRisk < 45;

    if (cumulusUsableHours >= 3 && maxCBH >= 600 && windGust <= 55 && precipProbMax < 45 && precipTotalMm < 2 && stormRisk < 45) {
      qualityScore = Math.max(qualityScore, maxVZ >= 1.2 ? 55 : 45);
    }
    if (
      cumulusUsableHours >= 2 &&
      maxVZ >= 1.6 &&
      maxCBH >= 1000 &&
      midCloudCover <= 25 &&
      highCloudCover <= 25 &&
      windGust <= 55 &&
      maxWind <= 38 &&
      precipProbMax < 45 &&
      precipTotalMm < 2 &&
      stormRisk < 45
    ) {
      qualityScore = Math.max(qualityScore, maxVZ >= 1.8 ? 70 : 65);
    }
    if (gustyCumulusHours >= 2 && maxVZ >= 1.0 && precipProbMax < 55) {
      qualityScore = Math.max(Math.min(qualityScore, 58), 42);
    }
    if (veiledCumulusHours >= 2) {
      qualityScore = Math.min(qualityScore, 52);
    }
    if (fastGrowthMainHours >= 1) {
      qualityScore = Math.min(qualityScore, 45);
    }
    if ((convectiveHours.length === 0 && visibleSoaringHours === 0 && localLowCeilingHours === 0 && pilotUsableLocalHours === 0) || maxVZ < 0.5 || (maxCBH < 300 && pilotUsableLocalHours < 3)) {
      qualityScore = Math.min(qualityScore, 25);
    }
    if (
      localLowCeilingHours >= 3 &&
      maxVZ >= 1.2 &&
      maxCBH >= 400 &&
      highCloudCover <= 25 &&
      precipProbMax < 45 &&
      precipTotalMm < 1 &&
      stormRisk < 45
    ) {
      qualityScore = Math.max(qualityScore, windGust > 55 || windConvectiveKmh > 60 ? 48 : 52);
    }
    if (realLocalSoaringWindow) {
      qualityScore = Math.max(qualityScore, maxVZ >= 1.8 ? 68 : 58);
    }
    if (pilotUsableLocalHours >= 4 && maxVZ >= 1.2 && precipProbMax < 50 && precipTotalMm < 2 && stormRisk < 55) {
      qualityScore = Math.max(qualityScore, maxVZ >= 1.6 ? 58 : 52);
    }
    if (pilotGoodLocalHours >= 3 && maxVZ >= 1.45 && windGust <= 60 && precipProbMax < 45 && precipTotalMm < 1.5 && stormRisk < 45) {
      qualityScore = Math.max(qualityScore, maxVZ >= 1.7 ? 62 : 56);
    }
    if (visibleSoaringHours >= 4 && maxVZ >= 1.6 && maxCBH >= 1100 && windGust <= 55 && precipProbMax < 45 && precipTotalMm < 2 && stormRisk < 45) {
      qualityScore = Math.max(qualityScore, maxVZ >= 1.9 ? 72 : 65);
    }
    if (realLocalSoaringWindow && windGust > 55) {
      qualityScore = Math.max(45, Math.min(qualityScore, 58));
    }

    // ── Verdict ───────────────────────────────────────────────────────────────
    const verdict = computeSoaringVerdict(
      maxCBH, maxVZ, stormRisk, dailyCape, li, maxWind,
      qualityScore, totalCloudCover, precipProbMax, precipTotalMm,
      cumulusUsableHours, midCloudCover, highCloudCover,
      dailyConvectiveInhibition
    );

    // ── DPV estimation ────────────────────────────────────────────────────────
    const convectiveWindowHours = convectiveHours.length; // 1 index = 1 hour
    // maxCBH is derived from the Hennig spread formula, so it is already an AGL estimate.
    const ceilingAGL = Math.max(0, maxCBH);
    const dpvKm = estimateDPV(
      maxVZ, ceilingAGL, convectiveWindowHours,
      windSurfaceSpeed, totalCloudCover, precipProbMax, stormRisk
    );

    // ── Flight potential label ────────────────────────────────────────────────
    const flightPotentialLabel = computeFlightPotentialLabel(
      qualityScore, maxVZ, ceilingAGL, dpvKm, stormRisk,
      precipProbMax, precipTotalMm, totalCloudCover
    );

    // ── Limiting factors ──────────────────────────────────────────────────────
    const limitingFactors = computeLimitingFactors(
      maxVZ, ceilingAGL, windSurfaceSpeed, windGust,
      totalCloudCover, middayTTdSpread, stormRisk, windConvectiveKmh,
      precipProbMax, precipTotalMm,
      dailyConvectiveInhibition, dailyBoundaryLayerHeight
    );
    if (cumulusUsableHours >= 3 && !limitingFactors.includes('Cumulus exploitables')) {
      limitingFactors.push('Cumulus exploitables');
    }
    if (gustyCumulusHours >= 2 && !limitingFactors.includes('Cumulus haches par le vent')) {
      limitingFactors.push('Cumulus haches par le vent');
    }
    if (veiledCumulusHours >= 2 && !limitingFactors.includes('Voile au-dessus des cumulus')) {
      limitingFactors.push('Voile au-dessus des cumulus');
    }
    if (fastGrowthMainHours >= 1 && !limitingFactors.includes('Developpement vertical rapide')) {
      limitingFactors.push('Developpement vertical rapide');
    }
    if (fastGrowthLateHours >= 1 && !limitingFactors.includes('Etalement ou cunimb possible en fin de journee')) {
      limitingFactors.push('Etalement ou cunimb possible en fin de journee');
    }

    // ── Confidence level ──────────────────────────────────────────────────────
    // Higher confidence for near-term days, lower for day 5-7
    const confidence = dayIdx === 0 ? 'Élevée' : dayIdx <= 2 ? 'Bonne' : dayIdx <= 4 ? 'Modérée' : 'Faible';

    const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const dateObj = new Date(dateStr + 'T12:00:00');
    const dayLabel = dayIdx === 0 ? "Aujourd'hui" : dayNames[dateObj.getDay()];

    const convectionStart = convectiveHours.length > 0 ? convectiveHours[0].hour : '--';
    const convectionEnd = convectiveHours.length > 0 ? convectiveHours[convectiveHours.length - 1].hour : '--';
    const triggerTemp = computeTriggerTemp(hourly.dew_point_2m?.[middayIdx] ?? 8);

    week.push({
      date: dateStr,
      dayLabel,
      verdict,
      qualityScore,
      sunrise,
      sunset,
      convectionStart,
      convectionEnd,
      maxCumulusBase: maxCBH,
      maxVZ,
      maxThermalStrength: maxThermal,
      stormRisk,
      waveConditions,
      convergence,
      overdevelopmentRisk,
      windShearFragmented,
      highCloudFilter,
      highCloudCover,
      tTdSpread: middayTTdSpread,
      precipProbabilityMax: precipProbMax,
      precipTotalMm,
      rainRiskLabel,
      rainWindowLabel,
      windSurface: { speed: windSurfaceSpeed, dir: windSurfaceDir },
      windGust,
      windAloft2000: {
        speed: windAloft120Speed !== null ? Math.round(windAloft120Speed) : windAloft80Speed,
        dir: windAloft120Dir !== null ? degToCompass(windAloft120Dir) : windAloft80Dir,
      },
      windAloft5000: { speed: wind5000ftSpeed, dir: wind5000ftDir },
      triggerTemp,
      tempMin,
      tempMax,
      blDepth: dailyBoundaryLayerHeight ?? maxCBH,
      cape: dailyCape,
      li,
      convectiveInhibition: dailyConvectiveInhibition,
      boundaryLayerHeight: dailyBoundaryLayerHeight,
      hourly: hourlyConditions,
      // New fields (v1.2.0)
      dpvKm,
      flightPotentialLabel,
      limitingFactors,
      confidence,
      modelSourceNote: weatherSource.note,
      modelSourceLabel: weatherSource.label,
    });
  }

  const reconciledWeek = week.map(reconcileVisibleSoaringSignal);
  if (reconciledWeek.length === 0 || reconciledWeek.every((day) => (day.hourly?.length ?? 0) === 0)) {
    throw new Error('Open-Meteo response does not contain usable hourly soaring data.');
  }
  const today = reconciledWeek[0];
  return { today, week: reconciledWeek };
}

// ─── Atmospheric sounding fetch ───────────────────────────────────────────────

export async function fetchAtmosphericSounding(
  aerodrome: AerodromeInfo,
  date: string
): Promise<SoundingLevel[]> {
  const { lat, lon } = aerodrome;
  const weatherSource = selectWeatherSource(aerodrome);

  const pressureVars = SOUNDING_PRESSURE_LEVELS.flatMap((p) => [
    `temperature_${p}hPa`,
    `relative_humidity_${p}hPa`,
    `wind_speed_${p}hPa`,
    `wind_direction_${p}hPa`,
    `geopotential_height_${p}hPa`,
  ]).join(',');

  const url = `${weatherSource.endpoint}?latitude=${lat}&longitude=${lon}&hourly=${pressureVars}&wind_speed_unit=kmh&timezone=auto&start_date=${date}&end_date=${date}`;

  let data: any;
  try {
    data = (await getCachedOpenMeteoForecast({ url, cacheTtlMs: DASHBOARD_CACHE_MS })).data;
  } catch (err) {
    if (weatherSource.id === 'global-best-match') throw err;
    const fallbackSource = WEATHER_SOURCES['global-best-match'];
    const fallbackUrl = `${fallbackSource.endpoint}?latitude=${lat}&longitude=${lon}&hourly=${pressureVars}&wind_speed_unit=kmh&timezone=auto&start_date=${date}&end_date=${date}`;
    data = (await getCachedOpenMeteoForecast({ url: fallbackUrl, cacheTtlMs: DASHBOARD_CACHE_MS })).data;
  }

  if (!data) return parseSoundingData({ hourly: {} }, SOUNDING_PRESSURE_LEVELS);

  if (!data) return parseSoundingData({ hourly: {} }, SOUNDING_PRESSURE_LEVELS);

  if (!data) return parseSoundingData({ hourly: {} }, SOUNDING_PRESSURE_LEVELS);

  return parseSoundingData(data, SOUNDING_PRESSURE_LEVELS);
}

function parseSoundingData(data: any, pressureLevels: number[]): SoundingLevel[] {
  const hourly = data.hourly;
  const idx = 12;

  return pressureLevels.map((p) => {
    const temp = hourly[`temperature_${p}hPa`]?.[idx] ?? null;
    const rh = hourly[`relative_humidity_${p}hPa`]?.[idx] ?? null;
    const dewpoint = (temp !== null && rh !== null) ? dewpointFromTempRh(temp, rh) : 0;
    const windSpeed = hourly[`wind_speed_${p}hPa`]?.[idx] ?? null;
    const windDir = hourly[`wind_direction_${p}hPa`]?.[idx] ?? null;
    const geoHeight = hourly[`geopotential_height_${p}hPa`]?.[idx] ?? PRESSURE_TO_ALT[p] ?? 0;

    return {
      pressure: p,
      altitude: Math.round(geoHeight),
      temp: temp !== null ? Math.round(temp * 10) / 10 : 0,
      dewpoint,
      windSpeed: windSpeed !== null ? Math.round(windSpeed) : 0,
      windDir: windDir !== null ? Math.round(windDir) % 360 : 0,
    };
  });
}

// ─── Hourly sounding (for emagram time slider) ────────────────────────────────

export async function fetchHourlySoundings(
  aerodrome: AerodromeInfo,
  date: string
): Promise<Record<number, SoundingLevel[]>> {
  const { lat, lon } = aerodrome;
  const weatherSource = selectWeatherSource(aerodrome);

  const pressureVars = FORECAST_PRESSURE_LEVELS.flatMap((p) => [
    `temperature_${p}hPa`,
    `relative_humidity_${p}hPa`,
    `wind_speed_${p}hPa`,
    `wind_direction_${p}hPa`,
    `geopotential_height_${p}hPa`,
  ]).join(',');

  const url = `${weatherSource.endpoint}?latitude=${lat}&longitude=${lon}&hourly=${pressureVars}&wind_speed_unit=kmh&timezone=auto&start_date=${date}&end_date=${date}`;

  let data: any;
  try {
    data = (await getCachedOpenMeteoForecast({ url, cacheTtlMs: DASHBOARD_CACHE_MS })).data;
  } catch (err) {
    if (weatherSource.id === 'global-best-match') throw err;
    const fallbackSource = WEATHER_SOURCES['global-best-match'];
    const fallbackUrl = `${fallbackSource.endpoint}?latitude=${lat}&longitude=${lon}&hourly=${pressureVars}&wind_speed_unit=kmh&timezone=auto&start_date=${date}&end_date=${date}`;
    data = (await getCachedOpenMeteoForecast({ url: fallbackUrl, cacheTtlMs: DASHBOARD_CACHE_MS })).data;
  }

  if (!data) return {};

  if (!data) return {};

  if (!data) return {};

  const hourly = data.hourly;
  const times: string[] = hourly.time || [];
  const result: Record<number, SoundingLevel[]> = {};

  times.forEach((t, idx) => {
    const hour = parseInt(t.split('T')[1]?.split(':')[0] || '0', 10);
    result[hour] = FORECAST_PRESSURE_LEVELS.map((p) => {
      const temp = hourly[`temperature_${p}hPa`]?.[idx] ?? 0;
      const rh = hourly[`relative_humidity_${p}hPa`]?.[idx] ?? 50;
      const dewpoint = dewpointFromTempRh(temp, rh);
      const windSpeed = hourly[`wind_speed_${p}hPa`]?.[idx] ?? 0;
      const windDir = hourly[`wind_direction_${p}hPa`]?.[idx] ?? 0;
      const geoHeight = hourly[`geopotential_height_${p}hPa`]?.[idx] ?? PRESSURE_TO_ALT[p] ?? 0;

      return {
        pressure: p,
        altitude: Math.round(geoHeight),
        temp: Math.round(temp * 10) / 10,
        dewpoint,
        windSpeed: Math.round(windSpeed),
        windDir: Math.round(windDir) % 360,
      };
    });
  });

  return result;
}

// ─── Fallback (when API fails) ────────────────────────────────────────────────

function buildFallbackDay(): DailySoaringForecast {
  const localDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0];
  return {
    date: localDate,
    dayLabel: "Aujourd'hui",
    verdict: 'marginal',
    qualityScore: 50,
    sunrise: '06:30',
    sunset: '20:30',
    convectionStart: '--',
    convectionEnd: '--',
    maxCumulusBase: 0,
    maxVZ: 0,
    maxThermalStrength: 0,
    stormRisk: 0,
    waveConditions: false,
    convergence: false,
    overdevelopmentRisk: false,
    windShearFragmented: false,
    highCloudFilter: false,
    highCloudCover: 0,
    tTdSpread: 10,
    precipProbabilityMax: 0,
    precipTotalMm: 0,
    rainRiskLabel: 'Pas de pluie significative',
    rainWindowLabel: 'Pas de pluie detectee pendant la fenetre de vol',
    windSurface: { speed: 15, dir: 'O' },
    windGust: 20,
    windAloft2000: { speed: 25, dir: 'O' },
    windAloft5000: { speed: 35, dir: 'O' },
    triggerTemp: 20,
    tempMin: 10,
    tempMax: 22,
    blDepth: 0,
    cape: 0,
    li: 0,
    hourly: [],
    dpvKm: 0,
    flightPotentialLabel: 'Non propice',
    limitingFactors: [],
    confidence: 'Modérée',
    modelSourceNote: WEATHER_SOURCES['global-best-match'].note,
    modelSourceLabel: WEATHER_SOURCES['global-best-match'].label,
  };
}

// ─── Geocoding search (Open-Meteo Geocoding API) ──────────────────────────────

export interface GeocodingResult {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  elevation: number;
  country: string;
  country_code: string;
  admin1?: string;
}

export async function searchAerodromeByName(query: string): Promise<AerodromeInfo[]> {
  if (query.length < 2) return [];

  const local = searchLocalAerodromes(query);
  if (local.length > 0) return local;

  try {
    const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=8&language=fr&format=json`;
    let res = await fetch(url);
    if (!res.ok) return [];
    let data = await res.json();

    const results: GeocodingResult[] = data.results || [];
    return results.map((r) => ({
      icao: r.country_code?.toUpperCase() + r.id.toString().slice(-2),
      name: r.admin1 ? `${r.name}, ${r.admin1}` : r.name,
      lat: r.latitude,
      lon: r.longitude,
      elevation: Math.round(r.elevation || 0),
      country: r.country_code?.toUpperCase() || '',
    }));
  } catch {
    return [];
  }
}
